import React, { useState, useRef, useEffect } from "react";
import { fmAlert, fmConfirm } from './dialog';
import { t as tr, aiIn, aiTextLang } from './i18n';
import {
  supabase, signOut, getSession, onAuthChange, deleteMyAccount, isEmail2FAEnabled,
  loadProfiles, createProfile, deleteProfile,
  loadPatient, savePatient, loadCycle, saveCycle, loadGut, saveGut,
  loadDoctors, saveDoctor, deleteDoctor,
  loadMedications, saveMedication, deleteMedication,
  loadMedLogs, saveMedLog, loadEvents, saveEventsDay,
  loadDocuments, saveDocument, updateDocument, updateDocumentFields, deleteDocument,
  loadAnalysisDocs, saveAnalysisDoc, deleteAnalysisDoc,
  loadMetrics, saveMetric, deleteMetric,
  loadSymptoms, saveSymptom, updateSymptom, deleteSymptom,
  loadVaccines, saveVaccine, deleteVaccine,
  loadFamily, saveFamily, deleteFamilyMember, findFamilyLinksForEmail,
  loadAISummary, saveAISummary,
  loadNotificationSettings, saveNotificationSettings,
  uploadFile, deleteFile, getDisplayUrl,
  getInvitationByToken, acceptInvitation, loadMyAccess,
  getOrCreateInvitation, autoAcceptIfUserExists, revokeInvitation, getActiveRoleForEmail
} from './supabase';
import { SYMPTOMS_LIST, PALETTE, ALL_ANALYTES } from './data';
import { todayISO, addDaysISO, fmtD, fmtChart, shouldMedBeActiveOnDate, isoD, asStrArr, confirmDelete, foldDia } from './helpers';
import { IconTrash, Modal, HeartSpinner } from './ui';
import { CSS } from './styles';
import { ExportTratamentForm, ExportRaportComplet } from './exports';
import { CicluTab, PeriodCalendar } from './CicluTab';
import { JurnalTab } from './JurnalTab';
import { AnalizeTab, InterpretAnalysesForm, CompareImagingForm, AddAnalysisManualForm, EditAnalysisValueForm } from './AnalizeTab';
import { VaccinuriTab, AddVaccineForm } from './VaccinuriTab';
import { RenameDocForm, MoveDocForm, DocumenteTab } from './DocumenteTab';
import { SumarAITab } from './SumarAITab';
import { FamilieTab } from './FamilieTab';
import { ProfileTab } from './ProfileTab';
import { FeedbackTab } from './FeedbackTab';
import { FisaTab } from './FisaTab';
import { TratamentTab, AddConditionForm, AddDoctorForm, AddEventForm, AddNoteForm, AddSymptomForm, DeleteAccountForm, AddProfileForm, AddMedDoseForm, AddMedForm, AddMetricForm, AddFamilyForm } from './views';
import { DebugScreen, ResetPasswordScreen, LoginScreen, ProfileSetupScreen, MfaGateScreen } from './auth';
import { runNotificationChecks, unlockAudio } from './notifications';
import { getSecondDegreeRelation, getInverseRelation, isHistoricDose, medHasUnnamedHistory, buildDayLog } from './logic';
import { registerSW } from './serviceWorkerRegistration';
registerSW();

// Hook care păstrează un text în localStorage (supraviețuiește blocării telefonului / reîncărcării PWA).
// Se golește explicit cu .clear() la salvare/anulare.

// ─── CONSTANTS ───────────────────────────────────────────────────────────────

const TAB_ICONS ={"Acasă":"👤","Ciclu":"🌸","Jurnal":"📔","Documente":"📁","Analize":"🔬","Tratament":"💊","Vaccinuri":"💉","Sumar AI":"✨","Familie":"👨‍👩‍👧","Medici":"🩺","Profil":"👤","Setări":"⚙️","Feedback":"💬"};
// Tab-uri private: vizibile DOAR pentru contul cu acest email (nu apar la îngrijitori/medici/profiluri partajate)
const PRIVATE_TABS_EMAIL = "cristinahasas@gmail.com";
// Eticheta afișată pentru un tab (cheia internă rămâne stabilă; „Acasă" e afișat ca „Profil")
const TAB_LABELS = {"Acasă":"Profil"};
const tabLabel = key => tr(TAB_LABELS[key] || key);
// Grupuri pentru meniul-sertar (ordinea din fiecare grup = ordinea afișată)
const NAV_GROUPS = [
  {title:"Dosarul meu", tabs:["Acasă","Ciclu","Jurnal","Analize","Tratament","Vaccinuri","Documente","Sumar AI"]},
  {title:"Cercul meu", tabs:["Familie","Medici"]},
  {title:"Contul meu", tabs:["Setări","Feedback"]},
];
// Scurtături mereu la vedere, în bara de jos (Ciclu la mijloc, la profil feminin)
const BOTTOM_TABS = ["Acasă","Ciclu","Jurnal","Tratament"];



// ─── HELPERS ─────────────────────────────────────────────────────────────────
// Escapează text înainte de a-l insera în HTML construit manual (document.write) — protecție împotriva
// conținutului generat de AI (potențial influențat de un document încărcat manipulator) care ar putea
// conține markup/script.
// Generează un fișier .ics (eveniment de calendar) pentru o programare — deschis pe telefon,
// îl adaugă în calendarul nativ, care se ocupă singur de reminder (sună și cu telefonul blocat).



// ─── AI CALL WITH RATE LIMITING + PDF SUPPORT ────────────────────────────────
let lastAICall = 0;
const AI_MIN_INTERVAL = 4500;

// Recalculează statusul (normal/high/low) DETERMINIST din valoare + intervalul de
// referință tipărit în document (mai sigur decât eticheta N/H/L a AI-ului). Dacă
// documentul nu are interval, cade pe intervalele cunoscute din ALL_ANALYTES.
// Întoarce null când nu se poate calcula (păstrăm atunci ce a zis AI-ul).
function recomputeStatus(value, refRange, name) {
  const n = parseFloat(String(value).replace(",", "."));
  if (isNaN(n)) return null;
  const r = String(refRange || "");
  // Fără semn negativ: cratima din interval (3.5-5.0) e separator, nu minus; valorile de laborator nu sunt negative
  const nums = (r.match(/\d+[.,]?\d*/g) || []).map(x => parseFloat(x.replace(",", ".")));
  let lo = null, hi = null;
  if (/[<≤]/.test(r) && nums.length >= 1) hi = nums[0];
  else if (/[>≥]/.test(r) && nums.length >= 1) lo = nums[0];
  else if (nums.length >= 2) { lo = Math.min(nums[0], nums[1]); hi = Math.max(nums[0], nums[1]); }
  if (lo == null && hi == null && name) {
    const fn = foldDia(name).replace(/\s*\([^)]*\)\s*/g, " ").trim();
    const a = ALL_ANALYTES.find(x => foldDia(x.name).replace(/\s*\([^)]*\)\s*/g, " ").trim() === fn);
    if (a) { if (a.min != null) lo = a.min; if (a.max != null) hi = a.max; }
  }
  if (lo == null && hi == null) return null;
  if (hi != null && n > hi) return "high";
  if (lo != null && n < lo) return "low";
  return "normal";
}

async function callAI(prompt, maxTokens=2000, fileBase64=null, fileMime=null) {
  // Circuit breaker: dacă tocmai am primit o eroare de cotă/rate-limit, nu mai lovim
  // deloc serverul până nu trece intervalul cerut. Fără asta, o remontare a formularului
  // sau reîncercările automate țineau cota blocată la infinit (val de 429-uri în consolă).
  if(window._aiCooldownUntil && Date.now() < window._aiCooldownUntil){
    const secs = Math.ceil((window._aiCooldownUntil - Date.now())/1000);
    window._lastAIError = `${tr("Limita gratuită AI încă activă. Mai așteaptă")} ~${secs} ${tr("secunde")}.`;
    return null;
  }
  const now = Date.now();
  const wait = AI_MIN_INTERVAL - (now - lastAICall);
  if(wait > 0) await new Promise(r => setTimeout(r, wait));
  lastAICall = Date.now();

  async function doCall() {
    const { data: { session } } = await supabase.auth.getSession();
    const r = await fetch('/claude', {
      method:'POST',
      headers:{'Content-Type':'application/json', 'Authorization': `Bearer ${session?.access_token || ''}`},
      body: JSON.stringify({ action:'ai', prompt, maxTokens, pdfBase64: fileBase64, fileMime })
    });
    if(r.status === 429){
      // Citește motivul: limitatorul nostru („Prea multe cereri…") vs. cota Gemini.
      // Fără _error, un 429 persistent făcea ca run() să primească {_retry} și să afișeze
      // un rezultat gol, fără mesaj (părea că „lucrează apoi nimic").
      let msg = tr('Prea multe cereri către AI. Încearcă din nou peste un minut.');
      let retryAfter = 0;
      try {
        const d = await r.json();
        if(d?.error){
          const raw = String(d.error);
          const m = raw.match(/retry in ([\d.]+)s/i);
          retryAfter = m ? Math.ceil(parseFloat(m[1])) : 0;
          if(/quota|exceeded|rate.?limit/i.test(raw)){
            // Cotă Gemini (plan gratuit, ~20 apeluri/minut, partajate de toată aplicația).
            msg = `${tr("Limita gratuită AI atinsă (~20 cereri/minut, partajate de toată aplicația). Încearcă din nou peste")} ${retryAfter?('~'+retryAfter+' '+tr('secunde')):tr('un minut')}.`;
          } else msg = raw;
        }
      } catch(e){}
      return { _rateLimited:true, _error:msg, _retryAfter:retryAfter };
    }
    let data;
    try { data = await r.json(); }
    catch(e){ console.error('AI: răspuns invalid de la server', e); return { _error:'Răspuns invalid de la server' }; }
    if(!r.ok || data.error){
      const msg = data?.error || `Eroare server (${r.status})`;
      console.error('AI server error:', msg);
      return { _error: msg };
    }
    const text = data.text || '{}';
    // Extrage doar partea JSON — Gemini uneori adaugă text înainte/după
    const extractJSON = (raw)=>{
      const s = raw.replace(/```json|```/g,"").trim();
      const firstBrace = s.search(/[{[]/);
      if(firstBrace===-1) return s;
      const lastBrace = Math.max(s.lastIndexOf('}'), s.lastIndexOf(']'));
      return lastBrace>firstBrace ? s.slice(firstBrace, lastBrace+1) : s.slice(firstBrace);
    };
    try {
      const cleaned = extractJSON(text);
      return JSON.parse(cleaned);
    } catch(e){
      // Recuperare JSON trunchiat — încearcă mai multe strategii
      const cleaned = extractJSON(text);
      // Strategia 1: array trunchiat — închide după ultimul obiect complet
      try {
        if(cleaned.startsWith('[')) {
          const lastComplete = cleaned.lastIndexOf('}');
          if(lastComplete > 0) {
            const parsed = JSON.parse(cleaned.slice(0, lastComplete+1) + ']');
            console.warn('AI: JSON trunchiat (array), recuperat', parsed.length);
            return parsed;
          }
        }
      } catch(e2){}
      // Strategia 2: obiect cu "values":[...] SAU "v":[...] (compact) trunchiat
      try {
        if(cleaned.startsWith('{')) {
          const valuesIdx = Math.max(cleaned.indexOf('"values"'), cleaned.indexOf('"v"'));
          if(valuesIdx > 0) {
            const lastComplete = cleaned.lastIndexOf('}');
            if(lastComplete > valuesIdx) {
              const parsed = JSON.parse(cleaned.slice(0, lastComplete+1) + ']}');
              console.warn('AI: JSON trunchiat (values), recuperat', (parsed.values||[]).length);
              return parsed;
            }
          }
        }
      } catch(e3){}
      // Strategia 3: obiect generic trunchiat — închide progresiv acolade/paranteze
      try {
        if(cleaned.startsWith('{')) {
          let s = cleaned;
          // Numără ghilimelele neescapate — impar înseamnă că am tăiat în MIJLOCUL unui string
          // (cazul cel mai frecvent: un câmp text lung, întrerupt brusc de limita de tokeni)
          const countUnescapedQuotes = (str)=>{
            let c=0;
            for(let i=0;i<str.length;i++){ if(str[i]==='"' && str[i-1]!=='\\') c++; }
            return c;
          };
          // Găsește poziția anterioară (virgulă/acoladă/paranteză) de la care putem arunca
          // o pereche cheie-valoare incompletă, fără să lăsăm JSON invalid în urmă.
          const backtrackTo = (str, fromIdx)=>{
            for(let i=fromIdx-1;i>=0;i--){ if(str[i]===','||str[i]==='{'||str[i]==='[') return i; }
            return -1;
          };
          if(countUnescapedQuotes(s) % 2 === 1){
            // String netermiant — aruncă tot de la ghilimeaua lui de deschidere înapoi la ultima virgulă/acoladă
            const cutPoint = backtrackTo(s, s.lastIndexOf('"'));
            if(cutPoint===-1) throw new Error('nimic de recuperat');
            s = s.slice(0, cutPoint+1);
          } else if(/:\s*$/.test(s)) {
            // Cheie fără valoare (tăiat exact după ":") — aruncă și cheia asta
            const cutPoint = backtrackTo(s, s.lastIndexOf(':'));
            if(cutPoint===-1) throw new Error('nimic de recuperat');
            s = s.slice(0, cutPoint+1);
          }
          s = s.replace(/,\s*$/,''); // virgulă rămasă înainte de închidere
          const openCurly = (s.match(/\{/g)||[]).length - (s.match(/\}/g)||[]).length;
          const openSquare = (s.match(/\[/g)||[]).length - (s.match(/\]/g)||[]).length;
          let fixed = s;
          for(let i=0;i<openSquare;i++) fixed += ']';
          for(let i=0;i<openCurly;i++) fixed += '}';
          const parsed = JSON.parse(fixed);
          console.warn('AI: JSON trunchiat (obiect generic), recuperat parțial');
          return parsed;
        }
      } catch(e4){}
      console.error('AI: JSON invalid în răspuns:', text.slice(0,300));
      return { _error:'AI a întors un format neașteptat' };
    }
  }

  try {
    const result = await doCall();
    if(result && result._rateLimited){
      // NU reîncerca — reîncercarea imediată doar menține cota blocată. Setează un cooldown
      // (respectă retryDelay-ul cerut de server) în care callAI se oprește din breaker-ul de sus.
      const cooldownSecs = result._retryAfter || 30;
      window._aiCooldownUntil = Date.now() + cooldownSecs*1000;
      window._lastAIError = result._error;
      console.warn(`AI rate-limited — cooldown ${cooldownSecs}s`);
      return null;
    }
    if(result && result._error){ window._lastAIError = result._error; return null; }
    return result;
  } catch(e) { console.error('AI call failed:', e); window._lastAIError = e.message; return null; }
}

// Normalizează câmpurile-listă din sumarul săptămânal: AI-ul le poate întoarce ca string,
// iar `.join`/`.map` din UI ar arunca „is not a function" → ecran negru. Aplicat și la
// generare, și la încărcarea din DB (ca sumarele vechi salvate greșit să nu mai crape).
function normWeeklyArrays(s){
  if(!s || typeof s!=="object") return s;
  ["missedMeds","medicationChanges","concerningPatterns","improvements","recommendations"].forEach(k=>{ s[k]=asStrArr(s[k]); });
  return s;
}

// Când AI-ul nu reușește să citească data din document, întreabă utilizatorul ce dată
// să folosească (în loc să pună tăcut data încărcării = azi). Întoarce ISO (YYYY-MM-DD).
function promptForDocDate(defaultISO){
  const input = window.prompt(tr("Nu am putut citi data din document.\nCe dată au aceste rezultate? (format ZZ.LL.AAAA)"), fmtD(defaultISO));
  if(!input) return defaultISO; // Anulat → păstrează data documentului
  const p = input.trim().split(".");
  if(p.length===3){
    const iso = `${p[2]}-${(p[1]||"").padStart(2,"0")}-${(p[0]||"").padStart(2,"0")}`;
    if(/^\d{4}-\d{2}-\d{2}$/.test(iso) && !isNaN(new Date(iso+"T12:00:00"))) return iso;
  }
  fmAlert(tr("Data introdusă nu e validă. Folosesc data documentului."));
  return defaultISO;
}

async function sendInvitationEmail(to, inviterName, profileName, role, token) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const r = await fetch('/claude', {
      method:'POST',
      headers:{'Content-Type':'application/json', 'Authorization': `Bearer ${session?.access_token || ''}`},
      body: JSON.stringify({
        action:'invite', to, inviterName, profileName, role, token,
        appUrl: window.location.origin
      })
    });
    const data = await r.json();
    return !data.error;
  } catch(e) { console.error('sendInvite failed:', e); return false; }
}

// ─── SEED DATA ────────────────────────────────────────────────────────────────
const SEED_PAT = {
  name:"", dob:"", bloodType:"", allergies:"",
  phone:"", emergencyContacts:[], conditions:[]
};

// ─── CSS ─────────────────────────────────────────────────────────────────────


// ═══════════════════════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════════════════════
export default function App() {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  // Poartă 2FA pe email: se cere codul DOAR ca pas 2 al unui login efectiv (după
  // email + parolă), nu la sesiuni restaurate/refresh. Setat de handler-ul de login.
  const [pendingMfa, setPendingMfa] = useState(false);

  const [recoveryMode, setRecoveryMode] = useState(false);

  useEffect(() => {
    // Detectează dacă userul a venit din linkul de resetare parolă
    if(typeof window !== "undefined") {
      const url = window.location.href;
      if(window.location.pathname.includes("reset") || url.includes("type=recovery")) {
        setRecoveryMode(true);
      }
    }
    getSession().then(s => { setSession(s); setAuthLoading(false); });
    const { data: { subscription } } = onAuthChange((event, s) => {
      // Supabase trimite acest eveniment când userul vine din linkul de recuperare
      if(event === "PASSWORD_RECOVERY") setRecoveryMode(true);
      // Supabase reîmprospătează tokenul (TOKEN_REFRESHED / SIGNED_IN) la fiecare revenire în
      // aplicație (ex: deblochezi telefonul). Dacă am reseta sesiunea de fiecare dată, MainApp
      // reîncărca TOT (ecranul „se încarcă"). Actualizăm doar când se schimbă efectiv userul
      // (login/logout) — tokenul e gestionat intern de clientul supabase, deci nu-l pierdem.
      setSession(prev => (prev?.user?.id === s?.user?.id ? prev : s));
    });
    return () => subscription.unsubscribe();
  }, []);

  // Check for invitation token in URL
  const [inviteToken, setInviteToken] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('invite');
    if(token) setInviteToken(token);
  }, []);

  if (authLoading) return (
    <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <style>{CSS}</style>
      <div className="heartspin" style={{fontSize:48,color:"var(--blue)"}}>♥</div>
    </div>
  );

  // Ecran debug — accesibil la orice URL care conține /debug
  if (typeof window !== "undefined" && window.location.pathname.includes("debug")) {
    return <DebugScreen/>;
  }

  // Ecran resetare parolă — userul vine din linkul de email (pathname SAU eveniment recovery)
  if (recoveryMode) {
    return <ResetPasswordScreen onDone={()=>setRecoveryMode(false)}/>;
  }

  if (!session) return <LoginScreen onLogin={(sess)=>{ setSession(sess); if(isEmail2FAEnabled(sess)) setPendingMfa(true); }}/>;

  if (pendingMfa) return (
    <MfaGateScreen session={session}
      onVerified={()=>setPendingMfa(false)}
      onCancel={async()=>{ await signOut(); setSession(null); setPendingMfa(false); }}/>
  );

  return <MainApp session={session} onSignOut={async()=>{await signOut();setSession(null);}} inviteToken={inviteToken}/>;
}


// ─── MAIN APP ─────────────────────────────────────────────────────────────────
function MainApp({session,onSignOut,inviteToken}) {
  const [tab, setTabState] = useState(() => { const t = localStorage.getItem('activeTab'); if(!t || t==="Fișă") return "Acasă"; if(t==="Profil") return "Setări"; return t; });
  const [profiles, setProfiles] = useState([]);
  const [activeProfileId, setActiveProfileIdState] = useState(() => localStorage.getItem('activeProfileId') || null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mainPatientName, setMainPatientName] = useState("");
  const [sharedAccess, setSharedAccess] = useState([]);
  const [sharedOwnerId, setSharedOwnerId] = useState(null); // user_id al owner-ului când vizualizez profil partajat
  const [sharedRole, setSharedRole] = useState(null); // 'caregiver' sau 'doctor'
  const [sharedSettings, setSharedSettings] = useState(null); // ce tab-uri sunt permise

  function setTab(t) {
    setTabState(t);
    localStorage.setItem('activeTab', t);
  }
  function setActiveProfileId(id) { setActiveProfileIdState(id); if(id) localStorage.setItem('activeProfileId', id); else localStorage.removeItem('activeProfileId'); setTab("Acasă"); }
  const [patient, setPatientState] = useState(SEED_PAT);
  const [editPat, setEditPat] = useState(false);
  const [doctors, setDoctorsState] = useState([]);
  const [events, setEventsState] = useState({});
  const [docs, setDocs] = useState([]);
  const [analysisDocs, setAnalysisDocs] = useState([]);
  const [imagingDocs, setImagingDocs] = useState([]);
  const [metrics, setMetricsState] = useState([]);
  const [meds, setMedsState] = useState([]);
  const [medLogs, setMedLogsState] = useState({});
  const [symptoms, setSymptomsState] = useState([]);
  const [vaccines, setVaccinesState] = useState([]);
  const [family, setFamilyState] = useState([]);
  const [aiSummary, setAiSummary] = useState(null);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [imagingCompare, setImagingCompare] = useState(null);
  const [annualSummary, setAnnualSummary] = useState(null);
  const [modal, setModal] = useState(null);
  // Butonul „back" (Android/browser): închide întâi modalul deschis, apoi meniul-sertar,
  // apoi revine la „Acasă" — abia după aceea iese din aplicație.
  const backNavRef = useRef({});
  backNavRef.current = { modal, drawerOpen, tab };
  useEffect(() => {
    window.history.pushState({ fmApp: true }, "");
    const onPop = () => {
      const st = backNavRef.current;
      if(st.modal){ setModal(null); window.history.pushState({fmApp:true}, ""); }
      else if(st.drawerOpen){ setDrawerOpen(false); window.history.pushState({fmApp:true}, ""); }
      else if(st.tab && st.tab !== "Acasă"){ setTabState("Acasă"); try{localStorage.setItem('activeTab',"Acasă");}catch(e){} window.history.pushState({fmApp:true}, ""); }
      // altfel: lasă back-ul implicit (iese din aplicație)
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const [needsProfileSetup, setNeedsProfileSetup] = useState(false);
  const [notifSettings, setNotifSettings] = useState(null);
  const [cycleData, setCycleDataState] = useState(null);
  function setCycleData(updater) {
    setCycleDataState(prev => {
      const next = typeof updater==='function' ? updater(prev) : updater;
      saveCycle(next, session, activeProfileId);
      return next;
    });
  }
  // Jurnal digestiv — privat, doar pentru contul PRIVATE_TABS_EMAIL
  const [gutData, setGutDataState] = useState(null);
  function setGutData(updater) {
    setGutDataState(prev => {
      const next = typeof updater==='function' ? updater(prev) : updater;
      // Jurnalul e legat exclusiv de profilul principal (profile_id null), niciodată de sub-profiluri
      saveGut(next, session, null).then(res=>{
        if(res && !res.ok){
          const hint = /schema cache|does not exist|relation/i.test(res.message||"")
            ? tr("\n\nTabelul gut_data pare să lipsească sau API-ul nu l-a preluat încă. Verifică în Supabase că SQL-ul din docs/gut-journal.sql a rulat, apoi Settings → API → „Reload schema”.")
            : "";
          fmAlert(tr("Jurnalul nu s-a putut salva în baza de date.\n\nMotiv: ") + (res.message||"?") + hint);
        }
      });
      return next;
    });
  }
  const [processing, setProcessing] = useState(false);
  const [processingMsg, setProcessingMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const fileRef = useRef();
  const fileCatRef = useRef("analize");
  const todayK = todayISO();
  const [selDay, setSelDay] = useState(todayK);

  // Handle invitation token
  useEffect(() => {
    if(!session || !inviteToken) return;
    async function handleInvite() {
      const inv = await getInvitationByToken(inviteToken);
      if(!inv || inv.status==='accepted') return;
      if(inv.invited_email !== session.user.email) {
        fmAlert(`${tr("Această invitație e pentru")} ${inv.invited_email}. ${tr("Ești logat cu")} ${session.user.email}.`);
        return;
      }
      const ok = await acceptInvitation(inviteToken, session.user.id);
      if(ok) {
        window.history.replaceState({}, '', window.location.pathname);
        const access = await loadMyAccess(session);
        setSharedAccess(access||[]);
        fmAlert(`${tr("Invitație acceptată! Ai acces ca")} ${inv.role==='caregiver'?tr('îngrijitor'):tr('medic')}.`);
      }
    }
    handleInvite();
  }, [session, inviteToken]);

  // Load shared access on startup
  useEffect(() => {
    if(!session) return;
    loadMyAccess(session).then(a => {
      // Exclude accesul partajat la propriul cont (owner == eu)
      const myId = session?.user?.id;
      const filtered = (a||[]).filter(acc => acc.owner_user_id !== myId);
      setSharedAccess(filtered);
    });
  }, [session]);

  // ── LOAD ALL DATA FROM SUPABASE ON STARTUP / PROFILE CHANGE ──
  useEffect(() => {
    if (!session) return;
    async function loadAll() {
      setLoading(true);
      try {
        const safe = p => p.catch(e => { console.warn('Load warning:', e?.message); return null; });
        const owner = sharedOwnerId; // null pt profil propriu, user_id owner pt profil partajat
        const [profs, pat, docs_, meds_, logs_, evs_, adocs_, metrics_, syms_, fam_, aidocs_, vacc_] = await Promise.all([
          safe(loadProfiles(session)),
          safe(loadPatient(session, activeProfileId, owner)), safe(loadDoctors(session, activeProfileId, owner)),
          safe(loadMedications(session, activeProfileId, owner)), safe(loadMedLogs(session, activeProfileId, owner)),
          safe(loadEvents(session, activeProfileId, owner)), safe(loadAnalysisDocs(session, activeProfileId, owner)),
          safe(loadMetrics(session, activeProfileId, owner)), safe(loadSymptoms(session, activeProfileId, owner)),
          safe(loadFamily(session, activeProfileId, owner)), safe(loadDocuments(session, activeProfileId, owner)),
          safe(loadVaccines(session, activeProfileId, owner))
        ]);
        setProfiles(profs||[]);
        // Încarcă mereu numele profilului principal (pt afișare în selector) - doar pt profil propriu
        if(activeProfileId && !owner) {
          const mainPat = await loadPatient(session, null);
          setMainPatientName(mainPat?.name || session?.user?.user_metadata?.full_name || "");
        }
        if(pat) {
          // Completează câmpurile lipsă (conditions, emergencyContacts etc.) ca să nu crape
          // ecranul Profil pe dosare vechi/parțiale care nu au tot ce așteaptă interfața.
          setPatientState({...SEED_PAT, ...pat});
          if(!activeProfileId) setMainPatientName(pat.name||"");
        } else {
          const meta = session?.user?.user_metadata || {};
          const fullName = meta.full_name || meta.name || "";
          const initialPat = {
            ...SEED_PAT,
            name: fullName,
            dob: meta.dob || "",
            sex: meta.sex || "",
            phone: meta.phone || "",
            emergencyContacts: Array.isArray(meta.emergencyContacts) ? meta.emergencyContacts : []
          };
          setPatientState(initialPat);
          if(!activeProfileId) setMainPatientName(fullName);
          // Salvează doar dacă avem măcar numele (cont creat cu formularul nou)
          if(fullName) await savePatient(initialPat, session, null);
        }
        // Verifică dacă profilul propriu necesită completarea datelor obligatorii
        // (ex: după login cu Google/Apple, datele nu sunt completate)
        if(!activeProfileId && !owner) {
          const checkPat = pat || {};
          const meta = session?.user?.user_metadata || {};
          const hasName = (checkPat.name || meta.full_name || meta.name || "").trim();
          const hasDob = (checkPat.dob || meta.dob || "").trim();
          const hasSex = (checkPat.sex || meta.sex || "").trim();
          const hasPhone = (checkPat.phone || meta.phone || "").trim();
          const hasEmergency = (Array.isArray(checkPat.emergencyContacts) && checkPat.emergencyContacts.some(e=>e.name&&e.phone))
            || (Array.isArray(meta.emergencyContacts) && meta.emergencyContacts.some(e=>e.name&&e.phone));
          setNeedsProfileSetup(!(hasName && hasDob && hasSex && hasPhone && hasEmergency));
        }
        if(docs_?.length) setDoctorsState(docs_); else setDoctorsState([]);
        if(meds_?.length) setMedsState(meds_); else setMedsState([]);
        if(logs_) setMedLogsState(logs_); else setMedLogsState({});
        if(evs_) setEventsState(evs_); else setEventsState({});
        if(adocs_?.length) setAnalysisDocs(adocs_); else setAnalysisDocs([]);
        if(metrics_?.length) setMetricsState(metrics_); else setMetricsState([]);
        if(syms_?.length) setSymptomsState(syms_); else setSymptomsState([]);
        if(vacc_?.length) setVaccinesState(vacc_); else setVaccinesState([]);
        if(fam_?.length) setFamilyState(fam_); else setFamilyState([]);
        if(aidocs_?.length) setDocs(aidocs_); else setDocs([]);
        // Reconstruiește imagingDocs din documentele cu rezumat AI salvat
        setImagingDocs((aidocs_||[]).filter(d=>d.type==="imagistica"&&d.aiSummary).map(d=>({docId:d.id,docName:d.name,date:d.date,summary:d.aiSummary})));
        const [aiS, weekS, annS, imgCmp] = await Promise.all([loadAISummary('main',session,activeProfileId,owner), loadAISummary('weekly',session,activeProfileId,owner), loadAISummary('annual_'+new Date().getFullYear(),session,activeProfileId,owner), loadAISummary('imaging_compare',session,activeProfileId,owner)]);
        if(aiS) setAiSummary(aiS); else setAiSummary(null);
        if(weekS) setWeeklySummary(normWeeklyArrays(weekS)); else setWeeklySummary(null);
        if(annS) setAnnualSummary(annS); else setAnnualSummary(null);
        if(imgCmp) setImagingCompare(imgCmp); else setImagingCompare(null);
        // Ciclu menstrual — privat, doar pe profilul propriu (nu partajat)
        if(!owner) {
          const cyc = await loadCycle(session, activeProfileId);
          setCycleDataState(cyc);
        } else {
          setCycleDataState(null);
        }
        // Jurnal digestiv — doar pe profilul PRINCIPAL al contului privat
        // (nu pe profilurile secundare/de familie, nu pe cele partajate)
        if(!owner && !activeProfileId && (session?.user?.email||"").toLowerCase()===PRIVATE_TABS_EMAIL) {
          const gut = await loadGut(session, null);
          setGutDataState(gut);
        } else {
          setGutDataState(null);
        }
        // Setări notificări (per profil)
        const ns = await loadNotificationSettings(session, activeProfileId, owner);
        setNotifSettings(ns);
      } catch(e) { console.error('Load error:', e); }
      setLoading(false);
    }
    loadAll();
  }, [session, activeProfileId, sharedOwnerId]);

  // Sincronizare legături de familie cross-cont (după email)
  // Când mă loghez, caut dacă cineva m-a adăugat în familia lui și creez legătura inversă la mine
  useEffect(()=>{
    async function syncFamilyLinks(){
      if(!session?.user?.email || activeProfileId || sharedOwnerId) return; // doar pe profilul principal propriu
      try {
        const links = await findFamilyLinksForEmail(session.user.email);
        if(!links || !links.length) return;
        const myFamily = await loadFamily(session, null);
        // Citește numele direct din DB (nu din state, care poate fi gol la acest moment)
        const myPat = await loadPatient(session, null);
        const myName = (myPat?.name) || patient.name || session.user.email.split("@")[0];
        let added = false;
        for(const link of links) {
          // link.my_relation_in_their_family = relația mea în familia lor (ex: "Fiică")
          // Trebuie să adaug profilul lor (ex: Viorel) în familia mea cu relația inversă
          const theirName = (link.source_profile_name || "").trim();
          // NU crea membri fără nume real sau cu nume placeholder "Profil"
          if(!theirName || theirName === myName || theirName.toLowerCase() === "profil") continue;
          // Relația inversă: dacă eu sunt "Fiică" la ei, ei sunt "Tată/Mamă" la mine (după sexul lor)
          const inverseRel = getInverseRelation(link.my_relation_in_their_family, link.their_sex);
          // Verifică dacă deja există (potrivire pe nume sau pe email, normalizat)
          const normD = s => (s||"").normalize("NFD").replace(/[\u0300-\u036f]/g,"").trim().toLowerCase();
          const nL = normD(theirName);
          const eL = (link.their_email||"").toLowerCase();
          const existing = myFamily.find(m=>
            (m.name && normD(m.name)===nL) ||
            (eL && m.email && m.email.toLowerCase()===eL)
          );
          if(!existing) {
            await saveFamily({
              name: theirName,
              relation: inverseRel,
              dob: link.their_dob || "",
              sex: link.their_sex || "",
              email: link.their_email || "",
              shareAccess: false,
              caregiver: false,
              conditions: [],
              profileId: null,
              _autoLinked: true
            }, session, null);
            added = true;
          } else if(existing._autoLinked && ((!existing.dob && link.their_dob) || (!existing.email && link.their_email) || (!existing.sex && link.their_sex))) {
            // Actualizează datele lipsă pentru o legătură auto-creată anterior
            await saveFamily({
              ...existing,
              dob: existing.dob || link.their_dob || "",
              sex: existing.sex || link.their_sex || "",
              email: existing.email || link.their_email || "",
              relation: existing.relation || inverseRel
            }, session, null);
            added = true;
          }
        }
        if(added) {
          const refreshed = await loadFamily(session, null);
          setFamilyState(refreshed);
        }
      } catch(e) { console.error('syncFamilyLinks:', e); }
    }
    syncFamilyLinks();
    // Intenționat fără `patient.name`: sincronizarea rulează la schimbarea contului/profilului,
    // nu la fiecare tastă când se editează numele.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, activeProfileId, sharedOwnerId, loading]);

  // Dacă tab-ul curent nu e permis pe profilul partajat, comută pe Fișă
  useEffect(()=>{
    // Profil = contul propriu; dacă intri pe un profil partajat, ieși din Profil
    if(sharedOwnerId && (tab==="Profil"||tab==="Setări"||tab==="Feedback")){ setTabState("Acasă"); return; }
    if(sharedOwnerId && sharedSettings){
      // Familie nu se arată niciodată medicului, indiferent ce e salvat în share_settings
      if(sharedRole==='doctor' && tab==="Familie"){ setTabState("Acasă"); return; }
      const tabMap = {"Acasă":"fisa","Documente":"documente","Analize":"analize","Tratament":"tratament","Vaccinuri":"vaccinuri","Familie":"familie","Sumar AI":"sumarAI"};
      const key = tabMap[tab];
      if(key && !sharedSettings[key]){
        setTabState("Acasă");
      }
    }
  }, [sharedOwnerId, sharedSettings, tab, sharedRole]);

  // ── MOTOR DE NOTIFICĂRI (în aplicație) ──
  // Ține datele curente într-un ref și rulează un singur interval care verifică
  // orele de medicamente / stocul / programările la fiecare minut.
  const notifDataRef = useRef({});
  notifDataRef.current = { ns: notifSettings, meds, events, patient, cycleData };
  useEffect(()=>{
    const tick=()=>{ try{ runNotificationChecks(notifDataRef.current); }catch(e){} };
    const id=setInterval(tick, 60000);
    tick();
    return ()=>clearInterval(id);
  }, []);
  // Deblochează audio la prima interacțiune, ca alarma programată să poată suna
  useEffect(()=>{
    const unlock=()=>{ unlockAudio(); window.removeEventListener("pointerdown",unlock); window.removeEventListener("touchstart",unlock); };
    window.addEventListener("pointerdown",unlock); window.addEventListener("touchstart",unlock);
    return ()=>{ window.removeEventListener("pointerdown",unlock); window.removeEventListener("touchstart",unlock); };
  }, []);

  // ── SETTERS PURI — fără side-effects, fără apeluri API ──────
  // Salvările se fac EXPLICIT la fiecare acțiune UI (onClick, onBlur etc.)

  function setPatient(updater) {
    setPatientState(prev => typeof updater==='function' ? updater(prev) : updater);
  }

  // Backup complet al datelor profilului curent, descărcat ca fișier JSON.
  // Documentele includ doar metadatele (nume/dată/sumar), nu și fișierele binare.
  function exportAllData() {
    const profileName = patient?.name || "profil";
    const data = {
      _meta: { app:"Fișă Medicală", exportedAt:new Date().toISOString(), profile:profileName, email:session?.user?.email||null },
      patient, doctors, medications:meds, medLogs, events, symptoms, metrics,
      analyses:analysisDocs,
      documents:(docs||[]).map(d=>({ id:d.id, name:d.name, type:d.type, date:d.date, aiSummary:d.aiSummary||null })),
      vaccines, family, cycle:cycleData, gut:gutData, aiSummary,
    };
    try {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type:"application/json" });
      const url = URL.createObjectURL(blob);
      const safe = String(profileName).replace(/[^a-z0-9]+/gi,"_").toLowerCase() || "profil";
      const a = document.createElement("a");
      a.href = url; a.download = `fisa-medicala_${safe}_${todayISO()}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(()=>URL.revokeObjectURL(url), 1000);
    } catch(e){ fmAlert(tr("Nu am putut genera fișierul de backup. Reîncearcă.")); }
  }

  // Restaurează datele dintr-un fișier de backup în profilul CURENT (cu confirmare).
  // Recomandat pe un profil gol (recuperare) — pe unul cu date existente poate crea duplicate.
  async function importBackup(file) {
    if(!file) return;
    let data;
    try { data = JSON.parse(await file.text()); }
    catch(e){ fmAlert(tr("Fișier invalid — nu este un backup JSON valid.")); return; }
    if(!data || typeof data!=="object"){ fmAlert(tr("Fișier invalid.")); return; }
    if(data._meta?.app !== "Fișă Medicală"){
      const okAnyway = await fmConfirm(tr("Fișierul nu pare un backup Fișă Medicală. Continui totuși?"), {danger:true, okText:"Continuă"});
      if(!okAnyway) return;
    }
    const ok = await fmConfirm(tr("Restaurez datele din backup în profilul curent?\n\nSe adaugă peste ce există acum — recomandat pe un profil gol. Datele personale (nume, adresă, diagnostice) vor fi suprascrise."), {danger:true, okText:"Restaurează"});
    if(!ok) return;
    setProcessing(true); setProcessingMsg(tr("Restaurez datele din backup…"));
    try {
      const pid = activeProfileId, own = sharedOwnerId;
      if(data.patient) await savePatient({...SEED_PAT, ...data.patient}, session, pid, own);
      for(const d of (data.doctors||[])) await saveDoctor(d, session, pid, own);
      for(const m of (data.medications||[])) await saveMedication(m, session, pid, own);
      for(const m of (data.metrics||[])) await saveMetric(m, session, pid, own);
      for(const a of (data.analyses||[])) await saveAnalysisDoc(a, session, pid, own);
      for(const v of (data.vaccines||[])) await saveVaccine(v, session, pid, own);
      for(const s of (data.symptoms||[])) await saveSymptom(s, session, pid, own);
      for(const fm of (data.family||[])) await saveFamily(fm, session, pid, own);
      for(const [dk,log] of Object.entries(data.medLogs||{})) await saveMedLog(dk, log, session, pid, own);
      for(const [dk,evs] of Object.entries(data.events||{})) await saveEventsDay(dk, evs, session, pid, own);
      if(data.cycle) await saveCycle(data.cycle, session, pid);
      if(data.gut) await saveGut(data.gut, session, pid);
      setProcessing(false);
      await fmAlert(tr("Restaurare completă. Reîncarc aplicația pentru a afișa datele."));
      window.location.reload();
    } catch(e) {
      setProcessing(false);
      fmAlert(tr("A apărut o eroare la restaurare: ") + (e?.message || e));
    }
  }
  function setDoctors(updater) {
    setDoctorsState(prev => typeof updater==='function' ? updater(prev) : updater);
  }
  function setMeds(updater) {
    setMedsState(prev => {
      const next = typeof updater==='function' ? updater(prev) : updater;
      // Salvează în DB medicamentele care s-au schimbat (activ/inactiv, ordine, etc.)
      try {
        next.forEach(m => {
          const old = prev.find(x => x.id === m.id);
          if(old && JSON.stringify(old) !== JSON.stringify(m)) {
            saveMedication(m, session, activeProfileId, sharedOwnerId);
          }
        });
      } catch(e) { console.error('setMeds save:', e); }
      return next;
    });
  }
  function setEvents(updater) {
    setEventsState(prev => typeof updater==='function' ? updater(prev) : updater);
  }
  function setMetrics(updater) {
    setMetricsState(prev => typeof updater==='function' ? updater(prev) : updater);
  }
  function setMedLogs(updater) {
    setMedLogsState(prev => typeof updater==='function' ? updater(prev) : updater);
  }
  function setSymptoms(updater) {
    setSymptomsState(prev => typeof updater==='function' ? updater(prev) : updater);
  }
  function setFamily(updater) {
    setFamilyState(prev => typeof updater==='function' ? updater(prev) : updater);
  }

  // Helper: actualizează evenimentele unei zile și salvează în DB
  async function saveEventsAndUpdate(dayK, updaterFn) {
    let toSave = null;
    setEventsState(prev => {
      const updated = updaterFn(prev[dayK]||[]);
      toSave = updated;
      return {...prev, [dayK]: updated};
    });
    if(toSave !== null) await saveEventsDay(dayK, toSave, session, activeProfileId, sharedOwnerId);
  }

  async function savePatientNow(pat) {
    await savePatient(pat, session, activeProfileId);
  }
  useEffect(() => {
    if (meds.length > 0) {
      const activeMedIds = new Set(meds.filter(m=>m.active).map(m=>String(m.id)));
      setMedLogsState(p => {
        const existing = p[todayK] || {};
        const expected = buildDayLog(meds, todayK);

        // Păstrează medicamentele active SAU cu intrări de istoric (confirmate/manuale)
        const cleaned = {};
        Object.entries(existing).forEach(([medId, doses]) => {
          const hasHistoric = (doses||[]).some(isHistoricDose);
          if(activeMedIds.has(medId) || hasHistoric) cleaned[medId] = doses;
        });

        // Adaugă dozele lipsă
        let changed = Object.keys(cleaned).length !== Object.keys(existing).length;
        Object.entries(expected).forEach(([medId, doses]) => {
          const cur = cleaned[medId];
          const scheduledDoses = (cur||[]).filter(d=>!d.note?.includes("manual"));
          if(!cur || scheduledDoses.length === 0) {
            const manual = (cur||[]).filter(d=>d.note?.includes("manual"));
            cleaned[medId] = [...doses, ...manual];
            changed = true;
          }
        });

        return changed ? {...p, [todayK]: cleaned} : p;
      });
    }
    // Intenționat pe `meds.length` (nu `meds`): reconstruiește planul zilei la adăugare/ștergere
    // de medicamente sau la schimbarea zilei, nu la fiecare re-creare a array-ului.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meds.length, todayK]);

  if(loading) return (
    <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:16}}>
      <style>{CSS}</style>
      <div className="heartspin" style={{fontSize:48,color:"var(--blue)"}}>♥</div>
      <div style={{color:"var(--text-3)",fontSize:15}}>{tr("Se încarcă datele...")}</div>
    </div>
  );

  // Completare obligatorie a datelor (ex. după login cu Google/Apple)
  if(needsProfileSetup) return (
    <ProfileSetupScreen
      session={session}
      onComplete={async(data)=>{
        const newPat = {...patient, ...SEED_PAT, name:data.name, dob:data.dob, sex:data.sex, phone:data.phone, city:data.city, county:data.county, country:data.country, emergencyContacts:data.emergencyContacts};
        setPatientState(newPat);
        setMainPatientName(data.name);
        await savePatient(newPat, session, null);
        try { await supabase.auth.updateUser({data:{full_name:data.name,dob:data.dob,sex:data.sex,phone:data.phone,city:data.city,county:data.county,country:data.country,emergencyContacts:data.emergencyContacts}}); } catch(e){}
        setNeedsProfileSetup(false);
      }}
      onSignOut={onSignOut}
    />
  );

  // Identitatea celui care introduce acum o intrare.
  // Dacă sunt îngrijitor (văd un profil partajat), întorc identitatea mea ca să se marcheze intrarea.
  // Dacă sunt owner-ul propriului profil, întorc null (intrările proprii nu se marchează).
  function currentAuthor(){
    if(!sharedOwnerId) return null; // sunt pe profilul meu
    const meta = session?.user?.user_metadata || {};
    return {
      addedBy: session?.user?.id || null,
      addedByName: meta.full_name || meta.name || session?.user?.email || tr("Îngrijitor"),
      addedByEmail: session?.user?.email || null
    };
  }
  function stampAuthor(obj){
    const a = currentAuthor();
    if(a && obj && typeof obj==="object"){ obj.addedBy = a.addedBy; obj.addedByName = a.addedByName; obj.addedByEmail = a.addedByEmail; }
    return obj;
  }

  function setDoseStatus(dayK, medId, schedIdx, status) {
    let stockDelta = 0;
    let logToSave = null;
    setMedLogsState(p => {
      const log = JSON.parse(JSON.stringify(p[dayK] || buildDayLog(meds, dayK)));
      if(!log[medId]) return p;
      const dose = log[medId].find(d => d.schedIdx===schedIdx);
      if(dose) {
        const prevStatus = dose.status;
        const qty = parseFloat(dose.dose)||1;
        dose.status = status;
        if(status==="done" || status==="skipped"){
          const medRef = meds.find(m=>String(m.id)===String(medId));
          if(medRef && !dose.medName) dose.medName = medRef.name;
          // Marchează cine a confirmat (dacă e îngrijitor)
          stampAuthor(dose);
        }
        if(status==="done") {
          dose.actualTime = new Date().toLocaleTimeString("ro-RO",{hour:"2-digit",minute:"2-digit",hour12:false});
          if(prevStatus !== "done") stockDelta = -qty;
        } else if(status==="pending" || status==="skipped") {
          if(prevStatus === "done") stockDelta = qty;
          dose.actualTime = "";
        }
      }
      logToSave = log;
      return {...p,[dayK]:log};
    });
    if(logToSave) saveMedLog(dayK, logToSave, session, activeProfileId, sharedOwnerId);
    if(stockDelta !== 0) {
      setMedsState(ms => {
        const updated = ms.map(m => m.id===medId ? {...m, stock:Math.max(0,(m.stock||0)+stockDelta)} : m);
        // Salvează doar medicamentul modificat
        const med = updated.find(m => m.id===medId);
        if(med) saveMedication(med, session, activeProfileId, sharedOwnerId);
        return updated;
      });
    }
  }

  // Confirmă toate dozele dintr-un slot orar — un singur setState + o singură salvare
  function setAllDoneForSlot(dayK, doseItems) {
    const now = new Date().toLocaleTimeString("ro-RO",{hour:"2-digit",minute:"2-digit",hour12:false});
    let logToSave = null;
    const stockDeltas = {};
    setMedLogsState(p => {
      const log = JSON.parse(JSON.stringify(p[dayK] || buildDayLog(meds, dayK)));
      doseItems.forEach(({dose, med}) => {
        if(!log[med.id]) return;
        const d = log[med.id].find(d => d.schedIdx===dose.schedIdx);
        if(d && d.status !== "done") {
          const qty = parseFloat(d.dose)||1;
          d.status = "done";
          d.actualTime = now;
          if(med && !d.medName) d.medName = med.name;
          stampAuthor(d); // marchează cine a confirmat (dacă e îngrijitor)
          stockDeltas[med.id] = (stockDeltas[med.id]||0) - qty;
        }
      });
      logToSave = log;
      return {...p,[dayK]:log};
    });
    // O singură salvare după toate modificările
    setTimeout(() => {
      if(logToSave) saveMedLog(dayK, logToSave, session, activeProfileId, sharedOwnerId);
      Object.entries(stockDeltas).forEach(([medId, delta]) => {
        if(delta !== 0) {
          setMedsState(ms => {
            const updated = ms.map(m => String(m.id)===String(medId) ? {...m, stock:Math.max(0,(m.stock||0)+delta)} : m);
            const med = updated.find(m => String(m.id)===String(medId));
            if(med) saveMedication(med, session, activeProfileId, sharedOwnerId);
            return updated;
          });
        }
      });
    }, 0);
  }

  async function onSaveVaccineGlobal(v) {
    const stamped = v._dbId ? v : stampAuthor({...v}); // marchează doar la creare, nu la editare
    const dbId = await saveVaccine(stamped, session, activeProfileId, sharedOwnerId);
    setVaccinesState(p => stamped._dbId ? p.map(x=>x._dbId===stamped._dbId?{...stamped}:x) : [...p, {...stamped, _dbId:dbId}]);
  }

  // Limita de mărime pentru un fișier urcat (după eventuala compresie a imaginilor)
  const MAX_UPLOAD_MB = 25;
  // Redimensionează/compresează imaginile mari înainte de urcare (JPEG, max 2000px).
  // PDF-urile și formatele care nu se pot desena pe canvas (HEIC/TIFF) rămân neschimbate.
  async function compressImage(file) {
    if(!file.type || !file.type.startsWith("image/")) return file;
    if(/heic|heif|tiff/i.test(file.type)) return file;
    try {
      const dataUrl = await new Promise((res,rej)=>{ const r=new FileReader(); r.onload=()=>res(r.result); r.onerror=rej; r.readAsDataURL(file); });
      const img = await new Promise((res,rej)=>{ const i=new Image(); i.onload=()=>res(i); i.onerror=rej; i.src=dataUrl; });
      const maxDim = 2000;
      const { width, height } = img;
      if(width<=maxDim && height<=maxDim && file.size < 1.5*1048576) return file; // deja mică
      const scale = Math.min(1, maxDim/Math.max(width,height));
      const w = Math.max(1,Math.round(width*scale)), h = Math.max(1,Math.round(height*scale));
      const canvas = document.createElement("canvas"); canvas.width=w; canvas.height=h;
      canvas.getContext("2d").drawImage(img,0,0,w,h);
      const blob = await new Promise(res=>canvas.toBlob(res,"image/jpeg",0.82));
      if(!blob || blob.size >= file.size) return file; // dacă nu câștigăm nimic, păstrăm originalul
      const newName = (file.name||"foto").replace(/\.(png|jpe?g|webp)$/i,"") + ".jpg";
      return new File([blob], newName, { type:"image/jpeg" });
    } catch(e){ return file; }
  }

  async function handleUpload(e) {
    const inp = e.target;
    const files = Array.from(inp.files || []);
    if(!files.length) return;
    // Anti-dublare: dacă același set de fișiere a fost deja procesat, ignoră
    const sig = files.map(f=>`${f.name}_${f.size}`).join("|");
    if(window._lastUploadSig === sig) return;
    window._lastUploadSig = sig;
    setTimeout(()=>{ if(window._lastUploadSig===sig) window._lastUploadSig=null; }, 5000);
    const cat = fileCatRef.current;
    const catLabel = {analize:"Analize",imagistica:"Imagistică",reteta:"Rețetă",altele:"Altele"}[cat]||"Altele";

    for(let idx=0; idx<files.length; idx++) {
      let f = files[idx];
      // Comprimă imaginile mari, apoi verifică limita de mărime
      f = await compressImage(f);
      if(f.size > MAX_UPLOAD_MB*1048576) {
        await fmAlert(`${tr("Fișierul")} „${f.name||tr("selectat")}" ${tr("este prea mare")} (${(f.size/1048576).toFixed(1)} MB). ${tr("Limita este")} ${MAX_UPLOAD_MB} MB.\n\n${tr("Pozele se micșorează automat; pentru un PDF prea mare, încearcă să-l comprimi sau să-l împarți în fișiere mai mici.")}`);
        continue;
      }
      // Nume sigur — pozele din galerie pot avea nume gol/generic
      const safeName = f.name && f.name.trim() ? f.name : `foto_${Date.now()}_${idx}.jpg`;
      // Verifică duplicat doar dacă numele e specific (nu generic de galerie)
      const isGeneric = /^(image|img|photo|foto)[._-]?\d*\.(jpg|jpeg|png|heic)$/i.test(safeName) || safeName.startsWith("foto_");
      const existing = !isGeneric ? docs.find(d => d.name === safeName) : null;
      if(existing) {
        const answer = await new Promise(resolve => {
          setModal({
            type:"duplicateFile",
            data:{ name:safeName, date:fmtD(existing.date) },
            onReplace: () => { setModal(null); resolve("replace"); },
            onSkip:    () => { setModal(null); resolve("skip"); },
          });
        });
        if(answer==="skip") continue;
        if(answer==="replace") {
          if(existing.fileUrl) deleteFile(existing.fileUrl);
          deleteDoc(existing.id);
        }
      }

      const localId = `local_${Date.now()}_${idx}_${Math.floor(Math.random()*1000000)}`;
      const localPreview = f.type.startsWith("image/") ? URL.createObjectURL(f) : null;
      const docObj = stampAuthor({
        id: localId, name:safeName, date:todayK, category:catLabel, type:cat,
        size:f.size>1048576?`${(f.size/1048576).toFixed(1)} MB`:`${Math.round(f.size/1024)} KB`,
        previewUrl: localPreview, isPdf:f.type==="application/pdf",
        aiExtracted:false, extracting:false, aiSummary:null, fileUrl:null
      });
      setDocs(p=>[...p, docObj]);

      try {
        const fileUrl = await uploadFile(f, session, sharedOwnerId);
        if(!fileUrl) throw new Error("Fișierul nu a putut fi urcat în stocare.");
        const realId = await saveDocument({...docObj, fileUrl}, session, activeProfileId, sharedOwnerId);
        // saveDocument întoarce null și când inserția e respinsă de politica de acces (RLS) —
        // fără verificarea asta, documentul pare încărcat dar dispare la refresh, fără nicio eroare
        if(!realId) throw new Error("Salvarea în baza de date a fost refuzată (verifică permisiunile de acces la acest profil).");
        // În baza de date se salvează identificatorul stabil; pentru afișare folosim URL semnat
        const displayUrl = await getDisplayUrl(fileUrl);
        setDocs(p=>p.map(d=>d.id===localId?{...d,id:realId,fileUrl:displayUrl,previewUrl:displayUrl||localPreview}:d));
      } catch(err) {
        window.fmErr && window.fmErr(`Upload eșuat pentru "${safeName}":`, err);
        fmAlert(`Documentul "${safeName}" NU a fost salvat.\n\n${err?.message||err}`);
        // scoate intrarea locală care a eșuat ca să nu rămână fantomă
        setDocs(p=>p.filter(d=>d.id!==localId));
      }
    }
    try { inp.value = ""; } catch(err){}
  }

  async function autoExtract(doc) {
    setDocs(p=>p.map(d=>d.id===doc.id?{...d,extracting:true}:d));

    // Tipuri suportate de Gemini direct (imagine/PDF)
    const VISUAL_TYPES = ['application/pdf','image/jpeg','image/jpg','image/png','image/heic','image/heif','image/tiff','image/webp'];
    // Tipuri text — extragem conținutul și îl trimitem ca text
    const TEXT_TYPES = ['text/plain','text/csv','application/csv'];
    let fileBase64 = null;
    let fileMime = null;
    let textContent = null;

    if(doc.fileUrl) {
      try {
        const resp = await fetch(doc.fileUrl);
        const blob = await resp.blob();
        const detectedMime = blob.type || doc.mimeType || '';

        if(VISUAL_TYPES.includes(detectedMime) || doc.isPdf || doc.isImage) {
          // PDF sau imagine — trimitem ca base64 la Gemini
          fileMime = detectedMime || (doc.isPdf ? 'application/pdf' : 'image/jpeg');
          fileBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          console.log('File loaded for AI extraction:', fileMime, Math.round(fileBase64.length/1024), 'KB');
        } else if(TEXT_TYPES.includes(detectedMime) || doc.name.match(/\.(csv|txt)$/i)) {
          // Text/CSV — citim ca text
          textContent = await blob.text();
          console.log('Text file loaded, chars:', textContent.length);
        } else if(detectedMime.includes('spreadsheet') || detectedMime.includes('excel') || /\.(xlsx|xls)$/i.test(doc.name)) {
          // Excel — parsăm cu SheetJS (deja în dependențe)
          try {
            const arrayBuf = await blob.arrayBuffer();
            const _xlsx = await import('xlsx'); const XLSX = _xlsx.default||_xlsx;
            const wb = XLSX.read(arrayBuf, {type:'array'});
            const allSheets = wb.SheetNames.map(name=>{
              const ws = wb.Sheets[name];
              return "=== " + name + " ===\n" + XLSX.utils.sheet_to_csv(ws);
            }).join("\n\n");
            textContent = allSheets.slice(0, 8000);
            console.log('Excel parsed, chars:', textContent.length);
          } catch(e) {
            console.warn('Excel parse error:', e);
          }
        } else if(/\.(docx|doc)$/i.test(doc.name)) {
          // Word — nu putem parsa fără library mare, informăm utilizatorul
          setDocs(p=>p.map(d=>d.id===doc.id?{...d,extracting:false}:d));
          fmAlert(tr("Nu pot extrage date din acest fișier Word (.doc/.docx).\n\nFișierele Word nu pot fi citite direct.\n\nSoluție: salvează ca PDF sau copiază conținutul într-un fișier .txt."));
          return;
        }
      } catch(e) {
        console.warn('Could not load file for AI extraction:', e);
      }
    }

    // Dacă nu avem conținut și nu e fișier vizual — nu putem extrage
    if(!fileBase64 && !textContent && !doc.isPdf && !doc.isImage) {
      setDocs(p=>p.map(d=>d.id===doc.id?{...d,extracting:false}:d));
      fmAlert(`Nu pot extrage date din "${doc.name}".

Formatul fișierului (${doc.name.split('.').pop().toUpperCase()}) nu poate fi citit direct de AI.

Soluție: convertește documentul în PDF sau imagine și reîncarcă-l.`);
      return;
    }

    // Format COMPACT — chei scurte ca să încapă mai multe analize în bugetul de tokeni
    const compactInstr = `Returnează DOAR JSON, format COMPACT cu chei scurte:
{"d":"data YYYY-MM-DD sau null","v":[{"n":"denumire","val":valoare,"u":"unitate","r":"interval ref","s":"N|H|L"}]}
unde s = N(normal)/H(crescut)/L(scăzut).
REGULI STRICTE:
- Extrage TOATE analizele din TOATE paginile documentului.
- Fiecare analiză O SINGURĂ DATĂ (dacă apare de mai multe ori, ia ultima valoare).
- "val" = DOAR numărul rezultatului (coloana de rezultat), fără unitate, cu punct zecimal (5.2 nu 5,2). NU confunda rezultatul cu intervalul de referință.
- "r" = intervalul de referință EXACT cum e tipărit (ex "3.5-5.0", "< 200", "> 40"). Dacă nu există, pune "".
- Folosește denumiri românești standard (Magneziu nu Magnesiu; Hemoglobină; Leucocite; etc).
- Pentru analize cu procent ȘI valoare absolută (ex Neutrofile), extrage-le ca intrări separate cu unitatea corectă (% vs 10^3/µL).
- Nu confunda analize cu nume asemănător (HDL vs LDL vs colesterol total; TSH vs T3 vs T4; ALT vs AST).
- DOAR valori reale din document, nu inventa. Dacă un câmp lipsește, pune "" sau null.
- Fii CONCIS: nu adăuga text în afara JSON-ului.`;

    let prompt;
    if(fileBase64) {
      prompt = `Ești un extractor de date medicale. Analizează acest fișier (${fileMime}) și extrage toate valorile de laborator și data documentului.
${compactInstr}`;
    } else if(textContent) {
      prompt = `Ești un extractor de date medicale. Analizează:

${textContent.slice(0, 12000)}

${compactInstr}`;
    }

    const rawResult = await callAI(prompt, 8000, fileBase64, fileMime);
    // Normalizează formatul compact înapoi la formatul complet
    let result = rawResult;
    if(rawResult && !rawResult._error) {
      if(Array.isArray(rawResult.v) || rawResult.d !== undefined) {
        const statusMap = {N:"normal",H:"high",L:"low"};
        result = {
          documentDate: rawResult.d || null,
          values: (rawResult.v||[]).map(x=>{
            const aiStatus = statusMap[x.s]||"normal";
            // Preferă statusul calculat din interval; păstrează-l pe cel AI doar când nu se poate calcula
            const calc = recomputeStatus(x.val, x.r, x.n);
            return {
              name: x.n, value: x.val, unit: x.u||"",
              referenceRange: x.r||"", status: calc || aiStatus
            };
          })
        };
      }
    }
    if(result===null){
      setDocs(p=>p.map(d=>d.id===doc.id?{...d,extracting:false}:d));
      fmAlert(tr("Extragerea AI a eșuat.\n\nMotiv: ") + (window._lastAIError || tr("necunoscut")));
      return;
    }
    // Suportă ambele formate: {documentDate, values:[...]} sau direct [...]
    let values = [];
    let extractedDate = null;
    if(Array.isArray(result)) {
      values = result;
    } else if(result && Array.isArray(result.values)) {
      values = result.values;
      // Validează formatul datei extrase
      if(result.documentDate && /^\d{4}-\d{2}-\d{2}$/.test(result.documentDate)) {
        extractedDate = result.documentDate;
      }
    }
    // Folosește data extrasă din document; dacă AI nu a găsit-o, întreabă utilizatorul
    // (fallback la data documentului doar dacă anulează / introduce o dată invalidă)
    const effectiveDate = extractedDate || promptForDocDate(doc.date);

    // DEDUPLICARE în cadrul aceluiași document — AI extrage uneori aceeași analiză de mai multe ori
    const seen = {};
    values = values.filter(v=>{
      if(!v.name) return false;
      const norm = String(v.name).trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/\s*\{[^}]*\}\s*/g," ").replace(/\s+/g," ").trim();
      const unit = String(v.unit||"").toLowerCase().replace(/\s+/g,"").replace("μ","µ");
      const k = norm+"|"+unit;
      if(seen[k]) return false; // păstrează prima apariție
      seen[k]=true;
      return true;
    });

    const metricMap=[{kw:["glicemie","glucoza"],f:"glicemie"},{kw:["sistolica"],f:"ts_sys"},{kw:["diastolica"],f:"ts_dia"},{kw:["greutate"],f:"greutate"}];
    const extracted={};
    values.forEach(v=>{const nl=(v.name||"").toLowerCase();metricMap.forEach(({kw,f})=>{if(kw.some(k=>nl.includes(k))){const n=parseFloat(String(v.value).replace(",","."));if(!isNaN(n))extracted[f]=n;}});});
    if(Object.keys(extracted).length){
      // O singură înregistrare în „Evoluție" pe data reală a documentului (effectiveDate).
      // Dacă există deja una la acea dată, o ACTUALIZĂM (update în DB pe _dbId) în loc să
      // inserăm un duplicat. Bug anterior: deduplica după doc.date (data încărcării) dar
      // salva cu effectiveDate (data din document) și insera mereu un rând nou → la fiecare
      // (re)extragere apărea un punct nou în grafic.
      const existing = metrics.find(m=>m.date===effectiveDate);
      const toSave = existing
        ? {...existing, ...extracted, source:"analize", sourceDoc:doc.name}
        : stampAuthor({date:effectiveDate, ...extracted, source:"analize", sourceDoc:doc.name});
      const dbId = await saveMetric(toSave, session, activeProfileId, sharedOwnerId);
      setMetricsState(p=>{
        const ex=p.find(m=>m.date===effectiveDate);
        if(ex) return p.map(m=>m.date===effectiveDate?{...m,...extracted,source:"analize",sourceDoc:doc.name}:m);
        return[...p,{...toSave,_dbId:dbId}].sort((a,b)=>a.date.localeCompare(b.date));
      });
    }
    if(values.length===0){
      // Nu marca documentul ca extras dacă nu s-a găsit nimic
      setDocs(p=>p.map(d=>d.id===doc.id?{...d,extracting:false}:d));
      fmAlert(tr("Nu s-au găsit valori de laborator în acest document.\n\nDacă documentul conține analize, verifică calitatea scanării sau încearcă din nou."));
      return;
    }
    const newAD = {docId:doc.id,docName:doc.name,date:effectiveDate,values};
    await saveAnalysisDoc(newAD, session, activeProfileId, sharedOwnerId);
    setAnalysisDocs(p=>[...p.filter(a=>a.docId!==doc.id),newAD]);
    await updateDocument(doc.id, {aiExtracted:true, aiSummary:null}, session, sharedOwnerId);
    setDocs(p=>p.map(d=>d.id===doc.id?{...d,extracting:false,aiExtracted:true}:d));
  }

  async function autoExtractImaging(doc) {
    setDocs(p=>p.map(d=>d.id===doc.id?{...d,extracting:true}:d));

    let fileBase64 = null;
    let fileMime = null;
    if(doc.fileUrl) {
      try {
        const resp = await fetch(doc.fileUrl);
        const blob = await resp.blob();
        const detectedMime = blob.type || 'image/jpeg';
        fileBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        fileMime = detectedMime;
      } catch(e) {
        console.warn('Could not load imaging file:', e);
      }
    }

    const prompt = `Ești un asistent medical. Analizează acest document de imagistică medicală ("${doc.name}").
Extrage informațiile din raportul radiologic/imagistic.
Returnează JSON: {"studyType":"tip studiu (CT/RMN/PET-CT/radiografie/ecografie)","bodyPart":"regiune","date":"data studiului în format YYYY-MM-DD sau gol","findings":"constatări","impression":"concluzie","recommendation":"recomandare"}
Extrage DOAR ce scrie în document, inclusiv data recoltării/efectuării studiului. Dacă nu poți citi documentul, returnează {"studyType":"necunoscut","bodyPart":"","date":"","findings":"Nu s-a putut citi documentul","impression":"","recommendation":""}.
Nu inventa date.`;
    const result = await callAI(prompt, 4000, fileBase64, fileMime);
    // callAI întoarce null când apelul eșuează (rate-limit / cheie / timeout). Fără această
    // verificare, `result.date` de mai jos arunca excepție → aplicația se albea (ecran negru),
    // iar documentul rămânea marcat „extras" fără conținut real.
    if(!result || result._error){
      setDocs(p=>p.map(d=>d.id===doc.id?{...d,extracting:false}:d));
      fmAlert(tr("Extragerea AI a eșuat.\n\nMotiv: ") + (window._lastAIError || tr("necunoscut")) + tr("\n\nÎncearcă din nou peste câteva secunde."));
      return;
    }
    await updateDocument(doc.id, {aiExtracted:true, aiSummary:result}, session, sharedOwnerId);
    // Data din raportul extras are prioritate (data studiului); dacă lipsește, întreabă
    const imgDate = (result.date && /^\d{4}-\d{2}-\d{2}$/.test(result.date)) ? result.date : promptForDocDate(doc.date);
    setImagingDocs(p=>[...p.filter(x=>x.docId!==doc.id),{docId:doc.id,docName:doc.name,date:imgDate,summary:result}]);
    setDocs(p=>p.map(d=>d.id===doc.id?{...d,extracting:false,aiExtracted:true,aiSummary:result}:d));
  }

  async function deleteDoc(id) {
    const docName = docs.find(d=>d.id===id)?.name || "documentul";
    try { await deleteDocument(id, session, sharedOwnerId); }
    catch(e){
      window.fmErr && window.fmErr('deleteDocument failed:', e);
      fmAlert(`Nu am putut șterge "${docName}" din baza de date. Verifică conexiunea și încearcă din nou.`);
      return;
    }
    setDocs(p=>p.filter(d=>d.id!==id));
    setAnalysisDocs(p=>p.filter(a=>a.docId!==id));
    setImagingDocs(p=>p.filter(a=>a.docId!==id));
    try { await deleteAnalysisDoc(id, session, sharedOwnerId); }
    catch(e){ window.fmErr && window.fmErr('deleteAnalysisDoc failed:', e); }
  }

  // Redenumire / mutare document (nume și/sau categorie)
  async function updateDoc(id, fields) {
    // category vine ca etichetă (Analize/Imagistică/...); type e cheia internă
    const catToType = {"Analize":"analize","Imagistică":"imagistica","Rețetă":"reteta","Altele":"altele"};
    const patch = {...fields};
    if(fields.category) patch.type = catToType[fields.category] || "altele";
    setDocs(p=>p.map(d=>d.id===id?{...d,...patch}:d));
    await updateDocumentFields(id, patch, session, sharedOwnerId);
  }

  // „Undo" extragere: șterge analiza extrasă din acest document ȘI valorile pe care le-a
  // pus în „Evoluție" (glicemie/tensiune/greutate), ca la anularea extragerii să dispară
  // de peste tot, nu doar din lista de analize.
  async function clearDocExtraction(docId) {
    const docName = docs.find(d=>d.id===docId)?.name;
    try { await deleteAnalysisDoc(docId, session, sharedOwnerId); }
    catch(e){ window.fmErr && window.fmErr('deleteAnalysisDoc failed:', e); fmAlert('Nu am putut șterge extragerea din baza de date. Încearcă din nou.'); return; }
    setAnalysisDocs(p=>p.filter(a=>a.docId!==docId));
    // Elimină măsurătorile din Evoluție provenite din acest document
    const fromDoc = metrics.filter(m=>m.source==="analize" && docName && m.sourceDoc===docName);
    for(const m of fromDoc){
      if(m._dbId){ try { await deleteMetric(m._dbId, session, sharedOwnerId); } catch(e){ window.fmErr && window.fmErr('deleteMetric (clear extraction) failed:', e); } }
    }
    if(fromDoc.length) setMetricsState(p=>p.filter(m=>!(m.source==="analize" && m.sourceDoc===docName)));
    await updateDocument(docId, {aiExtracted:false, aiSummary:null}, session, sharedOwnerId);
    setDocs(p=>p.map(d=>d.id===docId?{...d,aiExtracted:false,aiSummary:null}:d));
  }

  async function triggerAISummary() {
    setProcessing(true); setProcessingMsg(tr("Generez sumar general AI..."));
    try {
    const allValues = analysisDocs.flatMap(a=>(a.values||[]).map(v=>({...v,date:a.date}))).sort((a,b)=>b.date.localeCompare(a.date));
    const imagingSummaries = imagingDocs.map(i=>({data:i.date,tip:i.summary?.studyType,regiune:i.summary?.bodyPart,concluzie:i.summary?.impression,recomandare:i.summary?.recommendation}));
    const activeMeds = meds.filter(m=>m.active).map(m=>({name:m.name,form:m.form,indication:m.indication,schedule:m.schedule}));
    // Aderență ultimele 30 zile — din confirmările reale de administrare (defensiv)
    const cutoff30 = isoD(new Date(Date.now()-30*864e5));
    let d30total=0, d30done=0, d30skipped=0;
    const perMed30 = {};
    try {
      Object.entries(medLogs||{}).filter(([k])=>k>=cutoff30&&k<=todayK).forEach(([k,log])=>{
        if(!log||typeof log!=='object') return;
        Object.entries(log).forEach(([medId,doses])=>{
          if(!Array.isArray(doses)) return;
          const med = meds.find(m=>String(m.id)===medId||m._dbId===medId);
          doses.forEach(d=>{
            if(!d) return;
            if(d.status==="done"){d30done++;d30total++; if(med) perMed30[med.name]=(perMed30[med.name]||0)+1;}
            else if(d.status==="skipped"){d30skipped++;d30total++;}
            else if(k<todayK){d30total++;}
          });
        });
      });
    } catch(e){ console.warn('adherence calc error:', e); }
    // Notițe libere ale pacientului (din evenimente-notiță și din notele de la doze)
    const noteList = [];
    Object.entries(events||{}).forEach(([dk,evs])=>{ (evs||[]).forEach(e=>{ if(e&&e.visitNote) noteList.push({data:dk, text:e.visitNote}); }); });
    Object.entries(medLogs||{}).forEach(([dk,log])=>{ if(log&&typeof log==='object') Object.values(log).forEach(doses=>{ if(Array.isArray(doses)) doses.forEach(d=>{ if(d&&d.note) noteList.push({data:dk, text:d.note}); }); }); });
    // Istoric familial: pentru rudele legate de un profil pe care îl dețin, iau automat
    // diagnosticele din profilul lor; pentru celelalte (ex: soră, fără acces) — ce am scris manual.
    const familyHistory = await Promise.all((family||[]).map(async f=>{
      let conditii = Array.isArray(f.conditions) ? [...f.conditions] : (f.conditions?[f.conditions]:[]);
      if(f.profileId){
        try {
          const fp = await loadPatient(session, f.profileId, null);
          const fromProfile = (fp?.conditions||[]).map(c=>typeof c==="string"?c:(c?.text||"")).filter(Boolean);
          if(fromProfile.length) conditii = [...new Set([...conditii, ...fromProfile])];
        } catch(e){}
      }
      return { relatie:f.relation, conditii };
    }));
    const prompt=`Ești un asistent medical AI. Analizează datele pacientului și generează un sumar medical complet ${aiIn()}.

Date pacient:
- Diagnostice: ${JSON.stringify(patient.conditions)}
- Plan de tratament (medicamente active cu schema de administrare): ${JSON.stringify(activeMeds)}
- Aderență la tratament ultimele 30 zile (din confirmările de administrare): ${d30done}/${d30total} doze confirmate luate (${d30total>0?Math.round(d30done/d30total*100):0}%), ${d30skipped} sărite. Doze confirmate per medicament: ${JSON.stringify(perMed30)}
- Valori analize recente (cele mai noi primele, max 40): ${JSON.stringify(allValues.slice(0,40))}
- Rapoarte imagistice extrase (CT/RMN/PET etc.): ${JSON.stringify(imagingSummaries)}
- Simptome recente (ultimele 10): ${JSON.stringify(symptoms.slice(-10))}
- Istoric familial: ${JSON.stringify(familyHistory)}
- Notițe libere ale pacientului (max 20, cele mai recente): ${JSON.stringify(noteList.slice(-20))}

INSTRUCȚIUNI IMPORTANTE:
- Folosește TOATE informațiile de mai sus și fă conexiuni logice între ele (diagnostice + tratament + analize + imagistică + simptome + notițe + istoric familial). Interpretează, nu doar enumera.
- Ține cont EXPLICIT de notițele libere ale pacientului — pot conține context clinic important.
- ISTORIC FAMILIAL: dacă o rudă are o afecțiune cu componentă ereditară (ex: orice cancer, boli cardiovasculare, diabet, boli autoimune), pune în "familyRiskRecommendations" recomandări CONCRETE de screening/prevenție pentru pacient, menționând ruda și afecțiunea (ex: „Tatăl are cancer — discută cu medicul despre screening pentru acel tip de cancer"). Dacă nu există istoric familial relevant, lasă lista goală.
- Folosește-ți cunoștințele medicale reale despre fiecare medicament (indicație, clasă, efecte). Butilscopolamina (Buscopan) = antispastic pentru dureri abdominale/colici. Lactuloza (Lactecon) și Senna = laxative pentru constipație. Nu inventa denumiri sau indicații greșite — dacă nu ești sigur de un medicament, spune doar ce reiese din planul de tratament.
- NU menționa numărul exact de doze. În loc de "a luat X doze", interpretează clinic: ex. "a crescut nevoia de [analgezic], ceea ce sugerează intensificarea durerii" sau "necesarul de laxative indică o constipație persistentă, probabil efect advers al analgezicelor opioide".
- Analizează EVOLUȚIA analizelor (compară aceeași analiză la date diferite) și a imagisticii.
- Corelează simptomele, tratamentul și analizele (ex. constipația indusă de opioide care necesită laxative regulat).
- NU menționa stocul de medicamente.
- Fii concret și clinic relevant, nu generic.\n- Folosește limbaj SIMPLU, pe înțelesul unui pacient fără studii medicale. Evită termenii medicali complicați sau explică-i pe scurt între paranteze. Scopul e să ajuți pacientul să înțeleagă, nu să-l încurci.

Returnează JSON cu exact această structură:
{
  "summary": "rezumat general al stării de sănătate în 2-3 propoziții",
  "insights": ["observație despre evoluția analizelor/imagisticii", "observație 2", "observație 3"],
  "warnings": ["avertisment clinic relevant", "avertisment 2"],
  "medicationNotes": ["interpretare clinică a nevoii/schimbărilor de medicație, fără numere de doze", "notă 2"],
  "familyRiskRecommendations": ["recomandare 1", "recomandare 2"],
  "generatedAt": ""
}
${aiTextLang()}`;
    const result = await callAI(prompt, 8000);
    if(result && !result._error) {
      const s = {...result, generatedAt:new Date().toLocaleString("ro-RO")};
      setAiSummary(s);
      saveAISummary('main', s, session, activeProfileId);
    } else {
      fmAlert(tr("Generarea sumarului AI a eșuat.\n\nMotiv: ") + (window._lastAIError || tr("necunoscut")));
    }
    } catch(e){ console.error('triggerAISummary error:', e); fmAlert(tr("Eroare la generarea sumarului: ")+e.message); }
    finally { setProcessing(false); }
  }

  async function generateWeeklySummary(periodDates) {
    setProcessing(true); setProcessingMsg(tr("Generez sumar AI..."));
    try {
    // Use provided period or default to current week
    let weekStartISO, weekEndISO, prevWeekStartISO, prevWeekEndISO, periodLabel;
    if(periodDates) {
      weekStartISO = periodDates.start;
      weekEndISO = periodDates.end;
      prevWeekStartISO = periodDates.prevStart;
      prevWeekEndISO = periodDates.prevEnd;
      periodLabel = periodDates.label;
    } else {
      const now = new Date();
      const weekStart = new Date(now); weekStart.setDate(now.getDate() - now.getDay() + (now.getDay()===0?-6:1));
      weekStart.setHours(0,0,0,0);
      weekStartISO = isoD(weekStart);
      weekEndISO = todayK;
      const prevWeekStart = new Date(weekStart); prevWeekStart.setDate(prevWeekStart.getDate()-7);
      prevWeekStartISO = isoD(prevWeekStart);
      prevWeekEndISO = isoD(new Date(weekStart.getTime()-86400000));
      periodLabel = `Săptămâna ${weekStartISO} - ${weekEndISO}`;
    }

    const weekLogs = Object.entries(medLogs).filter(([k])=>k>=weekStartISO&&k<=weekEndISO);
    const prevWeekLogs = Object.entries(medLogs).filter(([k])=>k>=prevWeekStartISO&&k<=prevWeekEndISO);
    const weekSymptoms = symptoms.filter(s=>s.date>=weekStartISO&&s.date<=weekEndISO);
    const prevWeekSymptoms = symptoms.filter(s=>s.date>=prevWeekStartISO&&s.date<=prevWeekEndISO);
    const weekDocs = docs.filter(d=>d.date>=weekStartISO&&d.date<=weekEndISO);
    const weekMetrics = metrics.filter(m=>m.date>=weekStartISO&&m.date<=weekEndISO);
    const prevWeekMetrics = metrics.filter(m=>m.date>=prevWeekStartISO&&m.date<=prevWeekEndISO);
    const weekEvents = Object.entries(events).filter(([k])=>k>=weekStartISO&&k<=weekEndISO).flatMap(([k,evs])=>evs.map(e=>({...e,date:k})));

    // Calculează aderență — DOAR zilele până azi inclusiv (nu ne raportăm la viitor)
    // Pentru azi: dozele pending nu se numără ca ratate (ziua e în desfășurare)
    let totalDoses=0, doneDoses=0, skippedDoses=0;
    try {
      weekLogs.filter(([k])=>k<=todayK).forEach(([k,log])=>{
        if(!log||typeof log!=='object') return;
        Object.values(log).flat().forEach(d=>{
          if(!d) return;
          if(d.status==="done"){doneDoses++;totalDoses++;}
          else if(d.status==="skipped"){skippedDoses++;totalDoses++;}
          else if(k<todayK){totalDoses++;}
        });
      });
    } catch(e){ console.warn('weekly dose calc:', e); }

    let prevTotalDoses=0, prevDoneDoses=0;
    try {
      prevWeekLogs.forEach(([,log])=>{
        if(!log||typeof log!=='object') return;
        Object.values(log).flat().forEach(d=>{if(!d)return;prevTotalDoses++;if(d.status==="done")prevDoneDoses++;});
      });
    } catch(e){ console.warn('prev week calc:', e); }

    // Doze per medicament (doar zilele până azi)
    const medDosesThis = {}; const medDosesPrev = {};
    try {
      weekLogs.filter(([k])=>k<=todayK).forEach(([,log])=>{
        if(!log||typeof log!=='object') return;
        Object.entries(log).forEach(([medId,doses])=>{
          if(!Array.isArray(doses)) return;
          const med = meds.find(m=>String(m.id)===medId||m._dbId===medId);
          if(med) medDosesThis[med.name]=(medDosesThis[med.name]||0)+doses.filter(d=>d&&d.status==="done").length;
        });
      });
      prevWeekLogs.forEach(([,log])=>{
        if(!log||typeof log!=='object') return;
        Object.entries(log).forEach(([medId,doses])=>{
          if(!Array.isArray(doses)) return;
          const med = meds.find(m=>String(m.id)===medId||m._dbId===medId);
          if(med) medDosesPrev[med.name]=(medDosesPrev[med.name]||0)+doses.filter(d=>d&&d.status==="done").length;
        });
      });
    } catch(e){ console.warn('perMed calc:', e); }
    // Medicamente "la nevoie" — nu se interpretează ca întrerupte
    const prnMeds = meds.filter(m=>m.active&&(String(m.schedule||"").toLowerCase().includes("nevoie")||m.prn)).map(m=>m.name);
    const daysElapsed = Math.min(7, Math.round((new Date(todayK)-new Date(weekStartISO))/(864e5))+1);

    const prompt=`Ești un asistent medical AI. Generează un sumar comparativ ${aiIn()}.

PERIOADA CURENTĂ (${weekStartISO} - ${weekEndISO}) — ${periodLabel}:
- Aderență: ${doneDoses}/${totalDoses} doze luate (${totalDoses>0?Math.round(doneDoses/totalDoses*100):0}%), ${skippedDoses} sărite
- Doze per medicament: ${JSON.stringify(medDosesThis)}
- Simptome: ${JSON.stringify(weekSymptoms.map(s=>({data:s.date,ora:s.time,simptome:s.items})))}
- Documente încărcate: ${JSON.stringify(weekDocs.map(d=>({name:d.name,category:d.category})))}
- Valori măsurate: ${JSON.stringify(weekMetrics)}
- Programări: ${JSON.stringify(weekEvents.map(e=>({spec:e.spec,date:e.date})))}

PERIOADA PRECEDENTĂ (${prevWeekStartISO} - ${prevWeekEndISO}) - pentru comparație:
- Aderență: ${prevDoneDoses}/${prevTotalDoses} doze luate (${prevTotalDoses>0?Math.round(prevDoneDoses/prevTotalDoses*100):0}%)
- Doze per medicament: ${JSON.stringify(medDosesPrev)}
- Simptome: ${JSON.stringify(prevWeekSymptoms.map(s=>({data:s.date,simptome:s.items})))}
- Valori măsurate: ${JSON.stringify(prevWeekMetrics)}

CONTEXT IMPORTANT:
- Perioada curentă este ÎN DESFĂȘURARE: au trecut doar ${daysElapsed} din 7 zile (azi: ${todayK}). NU concluziona că dozele au scăzut sau tratamentul s-a întrerupt din cauza zilelor rămase — compară proporțional sau menționează explicit că săptămâna e incompletă.
- Medicamente "la nevoie" (NU se interpretează ca întrerupte dacă nu s-au administrat): ${JSON.stringify(prnMeds)}. Pentru acestea spune doar "nu a fost necesar în această perioadă" sau compară frecvența utilizării.

INSTRUCȚIUNI DE INTERPRETARE CLINICĂ:
- Folosește-ți cunoștințele medicale reale despre medicamente. Ex: Butilscopolamina (Buscopan)=antispastic pentru colici/dureri abdominale; Lactuloza (Lactecon), Senna=laxative pentru constipație; opioidele cauzează frecvent constipație (efect advers). NU inventa denumiri sau indicații greșite.
- NU cita numărul exact de doze. Interpretează CLINIC: ex "necesarul crescut de [analgezic] sugerează intensificarea durerii" sau "constipația persistă, probabil efect advers al analgezicelor, necesitând laxative regulat".
- Corelează utilizarea medicamentelor "la nevoie" cu simptomele raportate.
- Folosește comparații ("față de perioada precedentă") doar unde sunt valide (ține cont de săptămâna incompletă).\n- Folosește limbaj SIMPLU, pe înțelesul unui pacient fără studii medicale. Evită termenii medicali complicați sau explică-i pe scurt între paranteze. Scopul e să ajuți pacientul să înțeleagă, nu să-l încurci.

Returnează JSON:
{
  "period": "${periodLabel} (${weekStartISO} - ${weekEndISO})",
  "adherenceSummary": "aderență cu comparație față de perioada precedentă",
  "adherencePercent": ${totalDoses>0?Math.round(doneDoses/totalDoses*100):0},
  "adherenceChange": "crescut|scăzut|stabil față de ${prevTotalDoses>0?Math.round(prevDoneDoses/prevTotalDoses*100):0}% perioada precedentă",
  "missedMeds": ["medicament sărit"],
  "medicationChanges": ["schimbare doze față de perioada precedentă"],
  "symptomsSummary": "simptome cu comparație",
  "metricsTrend": "tendință valori cu comparație",
  "concerningPatterns": ["pattern îngrijorător"],
  "improvements": ["îmbunătățire față de perioada precedentă"],
  "recommendations": ["recomandare concretă"],
  "overallStatus": "bun|atentie|ingrijorator"
}`;
    const result = await callAI(prompt, 8000);
    if(result && !result._error) {
      const s = normWeeklyArrays({...result, generatedAt:new Date().toLocaleString("ro-RO")});
      setWeeklySummary(s);
      saveAISummary('weekly', s, session, activeProfileId);
    } else {
      fmAlert(tr("Generarea sumarului a eșuat.\n\nMotiv: ") + (result?._error || window._lastAIError || tr("necunoscut")));
    }
    } catch(e){ console.error('weekly error:', e); fmAlert(tr("Eroare: ")+e.message); }
    finally { setProcessing(false); }
  }

  async function generateAnnualSummary(selectedYear) {
    setProcessing(true); setProcessingMsg(tr("Generez sumar anual AI..."));
    try {
    const year = selectedYear || new Date().getFullYear();
    const yearStart = `${year}-01-01`;
    const yearEnd = `${year}-12-31`;
    const prevYearStart = `${year-1}-01-01`;
    const prevYearEnd = `${year-1}-12-31`;

    const yearDocs = docs.filter(d=>d.date>=yearStart&&d.date<=yearEnd);
    const prevYearDocs = docs.filter(d=>d.date>=prevYearStart&&d.date<=prevYearEnd);
    const yearMetrics = metrics.filter(m=>m.date>=yearStart&&m.date<=yearEnd);
    const prevYearMetrics = metrics.filter(m=>m.date>=prevYearStart&&m.date<=prevYearEnd);
    const yearSymptoms = symptoms.filter(s=>s.date>=yearStart&&s.date<=yearEnd);
    const prevYearSymptoms = symptoms.filter(s=>s.date>=prevYearStart&&s.date<=prevYearEnd);
    const yearEvents = Object.entries(events).filter(([k])=>k>=yearStart&&k<=yearEnd).flatMap(([k,evs])=>evs.map(e=>({...e,date:k})));
    const prevYearEvents = Object.entries(events).filter(([k])=>k>=prevYearStart&&k<=prevYearEnd).flatMap(([k,evs])=>evs.map(e=>({...e,date:k})));
    const yearValues = analysisDocs.filter(a=>a.date>=yearStart&&a.date<=yearEnd).flatMap(a=>(a.values||[]).map(v=>({...v,date:a.date})));
    const prevYearValues = analysisDocs.filter(a=>a.date>=prevYearStart&&a.date<=prevYearEnd).flatMap(a=>(a.values||[]).map(v=>({...v,date:a.date})));

    // Group metrics by month
    const metricsByMonth = {};
    yearMetrics.forEach(m=>{
      const month = m.date.slice(0,7);
      if(!metricsByMonth[month]) metricsByMonth[month]={glicemie:[],ts_sys:[],ts_dia:[],greutate:[]};
      if(m.glicemie) metricsByMonth[month].glicemie.push(m.glicemie);
      if(m.ts_sys) metricsByMonth[month].ts_sys.push(m.ts_sys);
      if(m.ts_dia) metricsByMonth[month].ts_dia.push(m.ts_dia);
      if(m.greutate) metricsByMonth[month].greutate.push(m.greutate);
    });

    const prompt=`Ești un asistent medical AI. Generează un sumar anual comparativ ${aiIn()}.

ANUL CURENT ${year}:
- Diagnostice: ${JSON.stringify(patient.conditions)}
- Medicamente active: ${JSON.stringify(meds.filter(m=>m.active).map(m=>m.name))}
- Documente încărcate: ${yearDocs.length} (${JSON.stringify(yearDocs.map(d=>({name:d.name,date:d.date,category:d.category})))})
- Valori pe luni: ${JSON.stringify(metricsByMonth)}
- Toate valorile analize: ${JSON.stringify(yearValues.slice(0,40))}
- Simptome: ${yearSymptoms.length} înregistrate (${JSON.stringify(yearSymptoms.slice(0,20))})
- Consultații: ${yearEvents.length} (${JSON.stringify(yearEvents.map(e=>({spec:e.spec,date:e.date,dr:e.dr})))})

ANUL PRECEDENT ${year-1} - pentru comparație:
- Documente: ${prevYearDocs.length}
- Valori analize: ${JSON.stringify(prevYearValues.slice(0,20))}
- Simptome: ${prevYearSymptoms.length} înregistrate
- Consultații: ${prevYearEvents.length}
- Metrici: ${JSON.stringify(prevYearMetrics.slice(0,10))}

INSTRUCȚIUNI: Fă comparații explicite între ${year} și ${year-1}. Menționează ce s-a schimbat, ce s-a îmbunătățit, ce s-a agravat. Folosește formule de tipul "față de ${year-1}", "comparativ cu anul trecut", "în creștere/scădere față de".

Returnează JSON:
{
  "year": ${year},
  "overallProgress": "evaluare generală comparativă ${year} vs ${year-1}",
  "keyEvents": ["eveniment medical important"],
  "conditionsEvolution": "cum au evoluat diagnosticele față de ${year-1}",
  "metricsEvolution": "tendință valori comparativ cu ${year-1}",
  "medicationChanges": "schimbări în tratament față de ${year-1}",
  "consultations": "consultații ${year} vs ${year-1}",
  "symptomsPattern": "pattern simptome ${year} vs ${year-1}",
  "improvements": ["îmbunătățire față de ${year-1}"],
  "concerns": ["îngrijorare față de ${year-1}"],
  "recommendationsNextYear": ["recomandare pentru ${year+1}"],
  "overallStatus": "bun|atentie|ingrijorator"
}`;
    const result = await callAI(prompt, 8000);
    if(result && !result._error) {
      const s = {...result, generatedAt:new Date().toLocaleString("ro-RO")};
      const withYear = {...s, year};
      setAnnualSummary(withYear);
      saveAISummary('annual_'+year, withYear, session, activeProfileId);
    } else {
      fmAlert(tr("Generarea sumarului anual a eșuat.\n\nMotiv: ") + (result?._error || window._lastAIError || tr("necunoscut")));
    }
    } catch(e){ console.error('annual error:', e); fmAlert(tr("Eroare: ")+e.message); }
    finally { setProcessing(false); }
  }

  // chartData separat per tip — doar datele cu valori reale, sortate crescător (stânga=vechi)
  const chartDataByType = {
    tensiune: metrics.filter(m=>m.ts_sys!=null||m.ts_dia!=null).sort((a,b)=>a.date.localeCompare(b.date)).map(m=>({date:fmtChart(m.date),...(m.ts_sys!=null?{Sistolică:m.ts_sys}:{}),...(m.ts_dia!=null?{Diastolică:m.ts_dia}:{})})),
    glicemie: metrics.filter(m=>m.glicemie!=null).sort((a,b)=>a.date.localeCompare(b.date)).map(m=>({date:fmtChart(m.date),Glicemie:m.glicemie})),
    greutate: metrics.filter(m=>m.greutate!=null).sort((a,b)=>a.date.localeCompare(b.date)).map(m=>({date:fmtChart(m.date),Greutate:m.greutate})),
    saturatie: metrics.filter(m=>m.saturatie!=null).sort((a,b)=>a.date.localeCompare(b.date)).map(m=>({date:fmtChart(m.date),"Saturație O₂":m.saturatie})),
    puls: metrics.filter(m=>m.puls!=null).sort((a,b)=>a.date.localeCompare(b.date)).map(m=>({date:fmtChart(m.date),Puls:m.puls})),
    temperatura: metrics.filter(m=>m.temperatura!=null).sort((a,b)=>a.date.localeCompare(b.date)).map(m=>({date:fmtChart(m.date),"Temperatură":m.temperatura})),
  };
  const chartData = [...metrics].sort((a,b)=>a.date.localeCompare(b.date)).map(m=>{
    const pt = {date:fmtChart(m.date)};
    if(m.ts_sys!=null) pt.Sistolică=m.ts_sys;
    if(m.ts_dia!=null) pt.Diastolică=m.ts_dia;
    if(m.glicemie!=null) pt.Glicemie=m.glicemie;
    if(m.greutate!=null) pt.Greutate=m.greutate;
    return pt;
  });

  // Vizibilitatea unui tab în funcție de profil (propriu/partajat, rol, sex)
  function tabVisible(t){
    if(t==="Ciclu"){ if(sharedOwnerId) return false; return (patient?.sex === "F"); }
    // Jurnal: strict privat — DOAR contul proprietar, DOAR pe profilul lui principal
    // (nu pe profilurile de familie create de el, nu pe profiluri partajate).
    if(t==="Jurnal"){ return !sharedOwnerId && !activeProfileId && (session?.user?.email||"").toLowerCase()===PRIVATE_TABS_EMAIL; }
    if(t==="Familie" && sharedRole==='doctor') return false;
    if(t==="Profil" || t==="Setări" || t==="Feedback") return !sharedOwnerId;
    if(!sharedOwnerId || !sharedSettings) return true;
    const tabMap = {"Acasă":"fisa","Documente":"documente","Analize":"analize","Tratament":"tratament","Vaccinuri":"vaccinuri","Familie":"familie","Sumar AI":"sumarAI","Medici":"medici"};
    const key = tabMap[t];
    if(!key) return true;
    return sharedSettings[key] === undefined ? true : !!sharedSettings[key];
  }

  return (
    <div style={{minHeight:"100vh",background:"var(--bg)",color:"var(--text)",fontFamily:"'Palatino Linotype',Palatino,serif"}}>
      <style>{CSS}</style>
      <div style={{width:"100%",maxWidth:780,margin:"0 auto",padding:"84px 20px 0"}}>
        <div style={{position:"fixed",top:0,left:0,right:0,zIndex:70,background:"var(--bg)",borderBottom:"1px solid var(--border)",display:"flex",justifyContent:"center"}}>
        <div style={{width:"100%",maxWidth:780,display:"flex",alignItems:"center",gap:14,padding:"14px 20px",boxSizing:"border-box"}}>
          <button onClick={()=>setDrawerOpen(true)} aria-label="Deschide meniul" title={tr("Meniu")}
            style={{width:50,height:50,borderRadius:"50%",background:activeProfileId?"linear-gradient(135deg,#7c3aed,var(--purple-line))":"linear-gradient(135deg,#8b5cf6,#6d28d9)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0,border:`2px solid ${activeProfileId?"#7c3aed":"#8b5cf6"}`,color:"#fff",cursor:"pointer",padding:0}}>♥</button>
          <div>
            {(()=>{
              const activeProf = activeProfileId ? profiles.find(p=>p.id===activeProfileId) : null;
              const displayName = patient.name || activeProf?.name || (activeProfileId ? "Profil nou" : "");
              return (
                <>
                  <div style={{fontSize:21,fontWeight:700}}>{displayName}</div>
                </>
              );
            })()}
          </div>
          <div style={{marginLeft:"auto",display:"flex",gap:6,flexWrap:"wrap",justifyContent:"flex-end",alignItems:"center"}}>
            {/* Profile selector */}
            <div style={{position:"relative"}}>
              <button className="bg" style={{fontSize:11,padding:"5px 10px",display:"flex",alignItems:"center",gap:6}} onClick={()=>setShowProfileMenu(v=>!v)}>
                <span style={{width:8,height:8,borderRadius:"50%",background:activeProfileId?"var(--purple)":"var(--blue)",display:"inline-block"}}/>
                {activeProfileId
                  ? (profiles.find(x=>x.id===activeProfileId)?.name||"Profil")
                  : (mainPatientName||session?.user?.email?.split("@")[0]||"Profil meu")}
                <span style={{fontSize:9}}>▾</span>
              </button>
              {showProfileMenu&&(
                <>
                <div onClick={()=>setShowProfileMenu(false)} style={{position:"fixed",inset:0,zIndex:99}}/>
                <div style={{position:"absolute",right:0,top:"calc(100% + 6px)",background:"var(--surface)",border:"1px solid var(--border-2)",borderRadius:12,padding:6,zIndex:100,minWidth:220,boxShadow:"0 8px 32px rgba(0,0,0,.6)"}}>
                  {/* Main profile */}
                  <button onClick={()=>{setSharedOwnerId(null);setSharedRole(null);setSharedSettings(null);setActiveProfileId(null);setShowProfileMenu(false);}} style={{display:"flex",alignItems:"center",gap:10,width:"100%",background:!activeProfileId?"var(--blue-bg)":"none",border:"none",color:"var(--text)",padding:"9px 12px",cursor:"pointer",borderRadius:8,fontSize:13,fontFamily:"inherit",textAlign:"left"}}>
                    <span style={{width:8,height:8,borderRadius:"50%",background:"var(--blue)",flexShrink:0}}/>
                    <div>
                      <div style={{fontWeight:600}}>{mainPatientName||session?.user?.email?.split("@")[0]||"Profil principal"}</div>
                    </div>
                    {!activeProfileId&&<span style={{marginLeft:"auto",color:"var(--blue)",fontSize:12}}>✓</span>}
                  </button>
                  {/* Other profiles */}
                  {profiles.map(p=>(
                    <div key={p.id} style={{display:"flex",alignItems:"center",borderRadius:8,background:activeProfileId===p.id?"var(--blue-bg)":"none"}}>
                      <button onClick={()=>{setSharedOwnerId(null);setSharedRole(null);setSharedSettings(null);setActiveProfileId(p.id);setShowProfileMenu(false);}} style={{display:"flex",alignItems:"center",gap:10,flex:1,background:"none",border:"none",color:"var(--text)",padding:"9px 12px",cursor:"pointer",fontSize:13,fontFamily:"inherit",textAlign:"left"}}>
                        <span style={{width:8,height:8,borderRadius:"50%",background:"var(--purple)",flexShrink:0}}/>
                        <div>
                          <div style={{fontWeight:600}}>{p.name}</div>
                          <div style={{fontSize:10,color:"var(--text-3)"}}>{p.relation}</div>
                        </div>
                        {activeProfileId===p.id&&<span style={{marginLeft:"auto",color:"var(--blue)",fontSize:12}}>✓</span>}
                      </button>
                    </div>
                  ))}
                  <div style={{height:1,background:"var(--border)",margin:"4px 0"}}/>
                  {/* Profiluri la care am acces ca îngrijitor/medic */}
                  {sharedAccess.length>0&&(
                    <>
                      <div style={{padding:"4px 12px",fontSize:10,color:"var(--text-4)"}}>{tr("Acces partajat")}</div>
                      {sharedAccess.map(acc=>(
                        <button key={acc.id} onClick={()=>{
                          // Profil partajat — rețin owner-ul ca să încarc datele lui
                          setSharedOwnerId(acc.owner_user_id);
                          setSharedRole(acc.role);
                          setSharedSettings(acc.share_settings||null);
                          setActiveProfileId(acc.profile_id);
                          setShowProfileMenu(false);
                        }} style={{display:"flex",alignItems:"center",gap:10,width:"100%",background:"none",border:"none",color:"var(--text)",padding:"9px 12px",cursor:"pointer",borderRadius:8,fontSize:13,fontFamily:"inherit",textAlign:"left"}}>
                          <span style={{width:8,height:8,borderRadius:"50%",background:"var(--green)",flexShrink:0}}/>
                          <div>
                            <div style={{fontWeight:600}}>{acc.profile_name||"Profil partajat"}</div>
                            <div style={{fontSize:10,color:"var(--text-3)"}}>{acc.role==='caregiver'?'👩‍⚕️ Îngrijitor':'👨‍⚕️ Medic'}</div>
                          </div>
                        </button>
                      ))}
                      <div style={{height:1,background:"var(--border)",margin:"4px 0"}}/>
                    </>
                  )}
                  <button onClick={()=>{setShowProfileMenu(false);setModal({type:"addProfile"});}} style={{display:"flex",alignItems:"center",gap:10,width:"100%",background:"none",border:"none",color:"var(--text-2)",padding:"9px 12px",cursor:"pointer",borderRadius:8,fontSize:13,fontFamily:"inherit"}}>
                    <span style={{fontSize:16}}>+</span> Profil nou
                  </button>
                </div>
                </>
              )}
            </div>
          </div>
        </div>
        </div>
        <div style={{marginBottom:14,fontSize:15,fontWeight:600,color:"var(--text-2)",display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:16}}>{TAB_ICONS[tab]}</span>{tabLabel(tab)}
        </div>
      </div>
      <div style={{width:"100%",maxWidth:780,margin:"0 auto",padding:"0 20px 90px",flex:1}} className="fin" key={tab}>
        {/* Îngrijitorul pe profil partajat are doar vizualizare la Fișă și Familie */}
        {(tab==="Acasă"||tab==="Medici")&&<FisaTab initialSub={tab==="Medici"?"medici":"date"} email={session?.user?.email||""} patient={patient} setPatient={setPatient} editPat={editPat} setEditPat={setEditPat} doctors={doctors} setDoctors={setDoctors} setModal={setModal} readOnly={sharedRole==='caregiver'||sharedRole==='doctor'} onSavePatient={(p)=>savePatient(p||patient,session,activeProfileId,sharedOwnerId)} loadPatientFresh={async()=>{const fresh=await loadPatient(session,activeProfileId,sharedOwnerId);if(fresh)setPatientState({...SEED_PAT, ...fresh});}} onDeleteDoctor={async (dbId)=>{
          try { await deleteDoctor(dbId, session, sharedOwnerId); }
          catch(e){ window.fmErr && window.fmErr('deleteDoctor failed:', e); fmAlert('Nu am putut șterge doctorul din baza de date. Verifică conexiunea și încearcă din nou.'); return; }
          setDoctorsState(p=>p.filter(x=>x._dbId!==dbId));
        }}/>}
        {tab==="Documente"&&<DocumenteTab family={family} docs={docs} deleteDoc={deleteDoc} updateDoc={updateDoc} fileRef={fileRef} fileCatRef={fileCatRef} handleUpload={handleUpload} imagingDocs={imagingDocs} setModal={setModal} readOnly={sharedRole==='doctor'} onExtract={async(doc)=>{ setProcessing(true); setProcessingMsg("Extrag datele din document cu AI…"); try{ if(doc.type==="imagistica")await autoExtractImaging(doc);else await autoExtract(doc); } finally{ setProcessing(false); } }}
          onClearExtraction={clearDocExtraction}
          savedDocOrder={patient.docOrder||{}}
          onSaveDocOrder={(order)=>{ setPatient(p=>{ const np={...p,docOrder:order}; savePatientNow(np); return np; }); }}
        />}
        {tab==="Analize"&&<AnalizeTab family={family} analysisDocs={analysisDocs} metrics={metrics} setMetrics={setMetrics} setModal={setModal} chartData={chartData} chartDataByType={chartDataByType} callAI={callAI} readOnly={sharedRole==='doctor'} onGoSettings={()=>setTab("Setări")}
          savedMetricOrder={patient.metricOrder}
          onSaveMetricOrder={(order)=>{ setPatient(p=>{ const np={...p,metricOrder:order}; savePatientNow(np); return np; }); }}
          onEditMetric={(m)=>setModal({type:"addMetric",data:{initial:m}})}
          onDeleteMetric={async(m)=>{
            if(m._dbId){
              try { await deleteMetric(m._dbId, session, sharedOwnerId); }
              catch(e){ window.fmErr && window.fmErr('deleteMetric failed:', e); fmAlert('Nu am putut șterge măsurătoarea din baza de date. Încearcă din nou.'); return; }
            }
            setMetricsState(p=>p.filter(x=>x._dbId!==m._dbId));
          }}
          onUpdateAnalysisValue={async(docId, newValues, newDate)=>{
            setAnalysisDocs(p=>p.map(a=>a.docId===docId?{...a,values:newValues,date:newDate||a.date}:a));
            const ad = analysisDocs.find(a=>a.docId===docId);
            if(ad) await saveAnalysisDoc({...ad,values:newValues,date:newDate||ad.date}, session, activeProfileId, sharedOwnerId);
          }}
          onClearExtraction={clearDocExtraction}
        />}
        {tab==="Tratament"&&<TratamentTab meds={meds} setMeds={setMeds} medLogs={medLogs} setMedLogs={setMedLogs} todayK={todayK} events={events} setEvents={setEvents} symptoms={symptoms} setSymptoms={setSymptoms} setDoseStatus={setDoseStatus} setModal={setModal} weeklySummary={weeklySummary} onWeeklySummary={generateWeeklySummary} selDay={selDay} setSelDay={setSelDay} family={family} readOnly={sharedRole==='doctor'}
          onDeleteMed={async(med)=>{
            if(med._dbId){
              try { await deleteMedication(med._dbId, session, sharedOwnerId); }
              catch(e){
                window.fmErr && window.fmErr('deleteMedication failed:', e);
                fmAlert(`Nu am putut șterge "${med.name}" din baza de date. Verifică conexiunea și încearcă din nou.`);
                return;
              }
            }
            setMedLogsState(p=>{
              const updated={};
              Object.entries(p).forEach(([day,log])=>{
                const doses=log[med.id]||[];
                const hasHistoric=doses.some(isHistoricDose);
                if(hasHistoric){ updated[day]=log; }
                else { const nl={...log}; delete nl[med.id]; updated[day]=nl; }
              });
              return updated;
            });
            setMedsState(ms=>ms.filter(m=>m.id!==med.id));
          }}
          onDeleteSymptom={async(s)=>{
            try { await deleteSymptom(s.id,session,sharedOwnerId); }
            catch(e){ window.fmErr && window.fmErr('deleteSymptom failed:', e); fmAlert('Nu am putut șterge simptomul din baza de date. Încearcă din nou.'); return; }
            setSymptomsState(p=>p.filter(x=>x.id!==s.id));
          }}
          onSaveEvent={(dayK,evs)=>saveEventsDay(dayK,evs,session,activeProfileId,sharedOwnerId)}
          onSaveMedLog={(dayK,log)=>saveMedLog(dayK,log,session,activeProfileId,sharedOwnerId)}
          onSaveMed={(med)=>saveMedication(med,session,activeProfileId,sharedOwnerId)}
          onToggleActive={async(med)=>{
            const todayStr=todayISO();
            const willActivate = !med.active;
            const updated = willActivate
              ? {...med, active:true, deactivatedAt:null, activeFrom:todayStr}
              : {...med, active:false, deactivatedAt:todayStr};
            // 1) actualizează în state + salvează medicamentul în Supabase (persistă la refresh)
            setMedsState(ms=>ms.map(m=>m.id===med.id?{...updated,_dbId:m._dbId}:m));
            await saveMedication(updated,session,activeProfileId,sharedOwnerId);
            // 2) curăță/regenerează log-urile pentru azi + viitor (păstrând intrările reale)
            setMedLogsState(p=>{
              const out={...p};
              Object.keys(out).forEach(day=>{
                if(day<todayStr) return; // trecutul rămâne neatins
                const dayLog=out[day]||{};
                const cur=dayLog[med.id]||[];
                // păstrează intrările reale (confirmate/sărite/manuale)
                const historic=cur.filter(isHistoricDose);
                let newDayLog={...dayLog};
                if(willActivate && shouldMedBeActiveOnDate(updated, day)){
                  // reactivat și programat în această zi → regenerează planul + păstrează intrările reale
                  const fresh=buildDayLog([updated], day)[med.id]||[];
                  newDayLog[med.id]=[...fresh,...historic];
                } else {
                  // dezactivat (sau zi neprogramată) → păstrează doar intrările reale
                  if(historic.length) newDayLog[med.id]=historic;
                  else delete newDayLog[med.id];
                }
                out[day]=newDayLog;
                saveMedLog(day,newDayLog,session,activeProfileId,sharedOwnerId);
              });
              return out;
            });
          }}
          setAllDoneForSlot={setAllDoneForSlot}
          session={session}
        />}
        {tab==="Vaccinuri"&&<VaccinuriTab family={family} vaccines={vaccines} setModal={setModal} readOnly={sharedRole==='doctor'}
          onDeleteVaccine={async(dbId)=>{
            try { await deleteVaccine(dbId,session,sharedOwnerId); }
            catch(e){ window.fmErr && window.fmErr('deleteVaccine failed:', e); fmAlert('Nu am putut șterge vaccinul din baza de date. Încearcă din nou.'); return; }
            setVaccinesState(p=>p.filter(x=>x._dbId!==dbId));
          }}/>}
        {tab==="Ciclu"&&<CicluTab cycleData={cycleData} setCycleData={setCycleData} setModal={setModal}/>}
        {tab==="Jurnal"&&<JurnalTab gutData={gutData} setGutData={setGutData} symptoms={symptoms} setModal={setModal} callAI={callAI}/>}
        {tab==="Familie"&&<FamilieTab family={family} setFamily={setFamily} setModal={setModal} readOnly={sharedRole==='caregiver'||sharedRole==='doctor'} aiSummary={aiSummary} onRefreshAI={triggerAISummary} onSwitchProfile={setActiveProfileId} onDeleteMember={async(dbId)=>{
          try { await deleteFamilyMember(dbId,session,sharedOwnerId); }
          catch(e){ window.fmErr && window.fmErr('deleteFamilyMember failed:', e); fmAlert('Nu am putut șterge membrul din baza de date. Încearcă din nou.'); return; }
          setFamilyState(p=>p.filter(x=>x._dbId!==dbId));
        }} profiles={profiles} sharedAccess={sharedAccess} onOpenShared={(acc)=>{setSharedOwnerId(acc.owner_user_id);setSharedRole(acc.role);setSharedSettings(acc.share_settings||null);setActiveProfileId(acc.profile_id);}}/>}
        {tab==="Sumar AI"&&<SumarAITab aiSummary={aiSummary} weeklySummary={weeklySummary} annualSummary={annualSummary} onRefresh={triggerAISummary} onWeekly={generateWeeklySummary} onAnnual={generateAnnualSummary}
          onLoadAnnualYear={async(y)=>{ const s=await loadAISummary('annual_'+y,session,activeProfileId,sharedOwnerId); setAnnualSummary(s||null); }}
          analysisDocs={analysisDocs} symptoms={symptoms} processing={processing} meds={meds} medLogs={medLogs} metrics={metrics} docs={docs} events={events}/>}
        {tab==="Setări"&&<ProfileTab initialSub="setari" session={session} patient={patient} notifSettings={notifSettings}
          onSaveNotif={async(next)=>{ setNotifSettings(next); await saveNotificationSettings(next, session, activeProfileId, sharedOwnerId); }}
          showCycle={patient?.sex==="F"} cycleNotify={cycleData?.notify}
          onSaveCycleNotify={(notify)=>setCycleData(prev=>({...(prev||{}), notify}))}
          activeProfileName={activeProfileId ? (profiles.find(p=>p.id===activeProfileId)?.name || patient?.name || "acest profil") : null}
          onDeleteProfile={activeProfileId ? (async()=>{
            const nm = profiles.find(p=>p.id===activeProfileId)?.name || tr("acest profil");
            confirmDelete(`${tr("Ștergi profilul")} "${nm}"? ${tr("Toate datele acestui profil vor fi pierdute. Contul tău rămâne.")}`, async()=>{
              try { await deleteProfile(activeProfileId); setProfiles(prev=>prev.filter(x=>x.id!==activeProfileId)); setActiveProfileId(null); }
              catch(e){ fmAlert(tr("Profilul nu a putut fi șters. Reîncearcă.")); }
            });
          }) : null}
          onDeleteAccount={()=>setModal({type:"deleteAccount"})} onSignOut={onSignOut} onExportData={exportAllData} onImportData={importBackup}/>}
        {tab==="Feedback"&&<FeedbackTab session={session} currentTab={tab}/>}
      </div>

      {/* ── BARĂ DE JOS — scurtături mereu la vedere ── */}
      <nav style={{position:"fixed",left:0,right:0,bottom:0,zIndex:80,background:"var(--surface)",borderTop:"1px solid var(--border-2)",display:"flex",justifyContent:"center",boxShadow:"0 -6px 20px -10px rgba(0,0,0,.45)"}}>
        <div style={{display:"flex",width:"100%",maxWidth:780,padding:"6px 8px calc(6px + env(safe-area-inset-bottom))"}}>
          {BOTTOM_TABS.filter(tabVisible).map(t=>{
            const active = tab===t;
            return (
              <button key={t} onClick={()=>setTab(t)} aria-current={active?"page":undefined} style={{flex:1,background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",
                display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:"4px 0"}}>
                <span style={{width:58,height:32,borderRadius:16,display:"flex",alignItems:"center",justifyContent:"center",fontSize:21,lineHeight:1,
                  background:active?"var(--blue-bg)":"transparent",transition:"background .2s"}}>{TAB_ICONS[t]}</span>
                <span style={{fontSize:11.5,color:active?"var(--blue)":"var(--text-3)",fontWeight:active?700:400}}>{tabLabel(t)}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* ── SERTAR (meniu complet) din inimioară ── */}
      {drawerOpen&&<div onClick={()=>setDrawerOpen(false)} style={{position:"fixed",inset:0,background:"rgba(3,6,11,.55)",zIndex:150}}/>}
      <aside style={{position:"fixed",top:0,left:0,bottom:0,width:"82%",maxWidth:300,zIndex:151,background:"var(--surface)",borderRight:"1px solid var(--border-2)",
        boxShadow:drawerOpen?"18px 0 46px -18px rgba(0,0,0,.5)":"none",transform:drawerOpen?"translateX(0)":"translateX(-102%)",transition:"transform .26s cubic-bezier(.22,.61,.36,1)",
        display:"flex",flexDirection:"column"}}>
        <div style={{display:"flex",alignItems:"center",gap:11,padding:"20px 16px 16px",borderBottom:"1px solid var(--border)"}}>
          <div style={{width:40,height:40,borderRadius:"50%",background:activeProfileId?"linear-gradient(135deg,#7c3aed,var(--purple-line))":"linear-gradient(135deg,#8b5cf6,#6d28d9)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:17}}>♥</div>
          <div style={{minWidth:0}}>
            <div style={{fontSize:15,fontWeight:700,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{patient.name||(activeProfileId?"Profil":mainPatientName)||"Profil"}</div>
            {sharedOwnerId&&<div style={{fontSize:11,color:"var(--text-3)"}}>{sharedRole==='caregiver'?'Îngrijitor':'Medic'} · partajat</div>}
          </div>
        </div>
        <nav style={{flex:1,overflowY:"auto",padding:"12px 10px"}}>
          {NAV_GROUPS.map(g=>{
            const items = g.tabs.filter(tabVisible);
            if(!items.length) return null;
            return (
              <div key={g.title}>
                <div style={{fontSize:11,fontWeight:600,letterSpacing:".01em",color:"var(--text-3)",padding:"12px 12px 5px"}}>{tr(g.title)}</div>
                {items.map(t=>(
                  <button key={t} onClick={()=>{setTab(t);setDrawerOpen(false);}}
                    style={{display:"flex",alignItems:"center",gap:12,width:"100%",textAlign:"left",fontFamily:"inherit",fontSize:14.5,cursor:"pointer",
                      background:tab===t?"var(--blue-bg)":"none",color:tab===t?"var(--text)":"var(--text-2)",border:"none",padding:"11px 12px",borderRadius:10}}>
                    <span style={{width:22,textAlign:"center",fontSize:15}}>{TAB_ICONS[t]}</span>{tabLabel(t)}
                    {tab===t&&<span style={{marginLeft:"auto",color:"var(--blue)",fontSize:12}}>●</span>}
                  </button>
                ))}
              </div>
            );
          })}
        </nav>
        <div style={{padding:12,borderTop:"1px solid var(--border)"}}>
          <button className="bg" style={{width:"100%"}} onClick={()=>{setDrawerOpen(false);onSignOut();}}>⎋ {tr("Deconectare")}</button>
        </div>
      </aside>

      {modal?.type==="duplicateFile"&&(
        <Modal title={tr("Fișier existent")} onClose={modal.onSkip}>
          <div style={{marginBottom:20}}>
            <div style={{fontSize:15,marginBottom:8}}>{tr("Fișierul")} <strong>"{modal.data?.name}"</strong> {tr("a fost deja încărcat pe")} <strong>{modal.data?.date}</strong>.</div>
            <div style={{fontSize:13,color:"var(--text-3)"}}>{tr("Ce vrei să faci?")}</div>
          </div>
          <div style={{display:"flex",gap:10}}>
            <button className="bp" style={{flex:1,background:"#7c3aed"}} onClick={modal.onReplace}>{tr("🔄 Înlocuiește")}</button>
            <button className="bg" style={{flex:1}} onClick={modal.onSkip}>{tr("⏭ Skip")}</button>
          </div>
        </Modal>
      )}
      {modal?.type==="deleteAccount"&&(
        <Modal title={tr("⚠ Șterge contul definitiv")} onClose={()=>setModal(null)}>
          <DeleteAccountForm
            email={session?.user?.email||""}
            onConfirm={async()=>{
              const res = await deleteMyAccount();
              if(res.ok){
                fmAlert(tr("Contul și toate datele au fost șterse."));
                window.location.href = window.location.origin;
              } else {
                fmAlert(tr("Eroare la ștergere: ") + (res.message||tr("necunoscută")));
              }
            }}
            onCancel={()=>setModal(null)}
          />
        </Modal>
      )}
      {modal?.type==="periodStart"&&(()=>{
        const todayK2=todayISO();
        return (
          <PeriodCalendar mode="start" periodLen={modal.data.periodLen} initialDays={[]}
            onClose={()=>setModal(null)}
            onSave={(days)=>{
              const sorted=[...new Set(days)].sort();
              const start=sorted.length?sorted[0]:todayK2;
              modal.data.setCycleData(prev=>{
                const p=prev||{};
                return {...p, periodActive:true, cycleStartDate:start, periods:[...new Set([...(p.periods||[]), ...sorted])]};
              });
              setModal(null);
            }}/>
        );
      })()}
      {modal?.type==="periodEnd"&&(()=>{
        const d=modal.data.cycleData||{};
        const start=modal.data.lastStart || d.cycleStartDate;
        const pLen=modal.data.periodLen||5;
        // zile reale marcate în ciclul curent (de la start încolo)
        const realDays=(d.periods||[]).filter(x=>!start||x>=start);
        // dacă nu există zile reale, pre-completează cu predicția (start + pLen zile)
        let initial=realDays;
        if(realDays.length===0 && start){
          initial=[];
          let cur=start;
          for(let i=0;i<pLen;i++){ initial.push(cur); cur=addDaysISO(cur,1); }
        }
        return (
          <PeriodCalendar mode="end" periodLen={pLen} initialDays={initial}
            onClose={()=>setModal(null)}
            onSave={(days)=>{
              const sorted=[...new Set(days)].sort();
              modal.data.setCycleData(prev=>{
                const p=prev||{};
                // păstrează zilele vechi (dinainte de start) + noile zile marcate
                const oldDays=(p.periods||[]).filter(x=>start&&x<start);
                const newStart=sorted.length?sorted[0]:start;
                return {...p, periodActive:false, cycleStartDate:newStart, periods:[...new Set([...oldDays, ...sorted])]};
              });
              setModal(null);
            }}/>
        );
      })()}
      {modal?.type==="renameDoc"&&(
        <Modal title={tr("Redenumește document")} onClose={()=>setModal(null)}>
          <RenameDocForm doc={modal.data} onSave={async(newName)=>{ await updateDoc(modal.data.id,{name:newName}); setModal(null); }}/>
        </Modal>
      )}
      {modal?.type==="moveDoc"&&(
        <Modal title={tr("Mută document")} onClose={()=>setModal(null)}>
          <MoveDocForm doc={modal.data} onSave={async(cat)=>{ await updateDoc(modal.data.id,{category:cat}); setModal(null); }}/>
        </Modal>
      )}
      {modal?.type==="addProfile"&&(
        <Modal title={tr("Profil nou")} onClose={()=>setModal(null)}>
          <AddProfileForm onAdd={async(name,relation,dob)=>{
            const p = await createProfile(session, name, relation, dob);
            if(p) {
              // Save patient data for new profile
              await savePatient({...SEED_PAT, name, dob}, session, p.id);
              
              // Add to family of current profile
              const myRelation = getInverseRelation(relation);
              const famMember = {name, relation, dob, email:"", shareAccess:false, caregiver:false, conditions:[], profileId:p.id};
              const dbId = await saveFamily(famMember, session, activeProfileId);
              setFamilyState(prev=>[...prev,{...famMember,_dbId:dbId}]);

              // Add current user to new profile's family (inverse)
              const myName = patient.name || "";
              const myDob = patient.dob || "";
              if(myName) {
                const inverseEntry = {name:myName, relation:myRelation, dob:myDob, email:session?.user?.email||"", shareAccess:false, caregiver:false, conditions:[], profileId:activeProfileId||null};
                await saveFamily(inverseEntry, session, p.id);
              }

              setProfiles(prev=>[...prev,p]);
              setModal(null);
              // Switch after modal close to avoid reload conflict
              setTimeout(()=>setActiveProfileId(p.id), 100);
            } else {
              setModal(null);
            }
          }}/>
        </Modal>
      )}
      {modal?.type==="addMedDose"&&(
        <Modal title={tr("Adaugă doză medicament")} onClose={()=>setModal(null)}>
          <AddMedDoseForm meds={meds} medLogs={medLogs} defaultDay={modal.data?.selDay||todayK} onAdd={(medId,time,dose,day)=>{
            const now = new Date().toLocaleTimeString("ro-RO",{hour:"2-digit",minute:"2-digit",hour12:false}).padStart(5,"0");
            const targetDay = day || modal.data?.selDay || todayK;
            const timeStr = (time||"").padStart(5,"0");
            const isPast = targetDay < todayK || (targetDay===todayK && timeStr && timeStr<=now);
            const status = isPast ? "done" : "pending";
            const actualTime = isPast ? timeStr : "";
            let logToSave = null;
            setMedLogsState(p=>{
              const log = JSON.parse(JSON.stringify(p[targetDay]||{}));
              if(!log[medId]) log[medId]=[];
              const _med2 = meds.find(m=>String(m.id)===String(medId));
              log[medId].push(stampAuthor({schedIdx:9000+Date.now(),time:time||now,dose:dose||"1",status,note:"",actualTime,medName:_med2?_med2.name:undefined}));
              logToSave = log;
              return {...p,[targetDay]:log};
            });
            // Salveaza in Supabase
            if(logToSave) saveMedLog(targetDay, logToSave, session, activeProfileId, sharedOwnerId);
            // Scade stoc doar dacă e marcat ca și confirmat
            if(status==="done") {
              setMedsState(ms=>{
                const updated = ms.map(m=>String(m.id)===String(medId)?{...m,stock:Math.max(0,(m.stock||0)-(parseFloat(dose)||1))}:m);
                const med = updated.find(m=>String(m.id)===String(medId));
                if(med) saveMedication(med, session, activeProfileId, sharedOwnerId);
                return updated;
              });
            }
            setModal(null);
          }}/>
        </Modal>
      )}
      {modal?.type==="addCondition"&&<Modal title={tr("Diagnostic nou")} onClose={()=>setModal(null)}><AddConditionForm onAdd={async c=>{
        const updated = {...patient, conditions:[...(patient.conditions||[]), c]};
        setPatientState(updated);
        await savePatient(updated, session, activeProfileId);
        setModal(null);
      }}/></Modal>}
      {modal?.type==="addVaccine"&&<Modal title={modal.data?._dbId?tr("Editează vaccin"):tr("Vaccin nou")} onClose={()=>setModal(null)}>
        <AddVaccineForm initial={modal.data} onAdd={async v=>{
          await onSaveVaccineGlobal(v);
          setModal(null);
        }}/>
      </Modal>}
      {modal?.type==="addDoctor"&&(
        <Modal title={modal.data?._dbId?tr("Editează medic"):tr("Medic nou")} onClose={()=>setModal(null)} wide>
          <AddDoctorForm initial={modal.data} checkRole={async(email)=>await getActiveRoleForEmail(session, activeProfileId, email)} onAdd={async d=>{
            const dbId = await saveDoctor({...d, _dbId: modal.data?._dbId}, session, activeProfileId);
            setDoctorsState(p => modal.data?._dbId
              ? p.map(x => x._dbId===modal.data?._dbId ? {...d,_dbId:modal.data?._dbId} : x)
              : [...p, {...d, _dbId:dbId}]
            );
            // Gestionare acces medic (aceeași logică: link o singură dată, auto-accept, revocare)
            if(d.email) {
              const profileName = patient.name || profiles.find(p=>p.id===activeProfileId)?.name || "Profil";
              const myName = patient.name || session?.user?.email?.split("@")[0] || "";
              if(d.sharing?.active) {
                // Verifică exclusivitatea rolului
                const existingRole = await getActiveRoleForEmail(session, activeProfileId, d.email);
                if(existingRole && existingRole !== 'doctor') {
                  fmAlert(`${tr("Adresa")} ${d.email} ${tr("are deja rol de îngrijitor pentru acest profil. O persoană nu poate fi și medic și îngrijitor simultan. Revocă întâi celălalt rol.")}`);
                  return;
                }
                const inv = await getOrCreateInvitation(session, activeProfileId, d.email, 'doctor', d.sharing);
                if(inv) {
                  const autoAccepted = await autoAcceptIfUserExists(inv.token);
                  if(autoAccepted) {
                    if(!inv._existing) fmAlert(`✓ ${d.name||tr("Medicul")} ${tr("are deja cont și a primit acces automat.")}`);
                    setModal(null);
                  } else if(inv._existing || inv._reactivated) {
                    if(inv._reactivated) fmAlert(`✓ ${tr("Acces reactivat pentru")} ${d.name||tr("medic")}.`);
                    setModal(null);
                  } else {
                    sendInvitationEmail(d.email, myName, profileName, 'doctor', inv.token).catch(()=>{});
                    const inviteUrl = `https://fisa-medicala.pages.dev?invite=${inv.token}`;
                    setModal({type:"showInviteLink", data:{url:inviteUrl, email:d.email, name:d.name||"medic"}});
                  }
                  return;
                }
              } else {
                // Partajare dezactivată — revocă accesul
                await revokeInvitation(session, activeProfileId, d.email, 'doctor');
              }
            }
            setModal(null);
          }}/>
        </Modal>
      )}
      {modal?.type==="addMetric"&&<Modal title={modal.data?.initial?tr("Editează valori"):tr("Înregistrare valori")} onClose={()=>setModal(null)}><AddMetricForm initial={modal.data?.initial} onAdd={async m=>{
        const editing = modal.data?.initial;
        if(editing?._dbId){
          // EDITARE: actualizează intrarea existentă
          const updated = stampAuthor({...editing, ...m, source:"manual"});
          await saveMetric(updated, session, activeProfileId, sharedOwnerId);
          setMetricsState(p=>p.map(x=>x._dbId===editing._dbId?updated:x).sort((a,b)=>a.date.localeCompare(b.date)));
        } else {
          // ADĂUGARE nouă
          const dbId=await saveMetric(stampAuthor({...m,source:"manual"}),session,activeProfileId,sharedOwnerId);
          setMetricsState(p=>[...p,stampAuthor({...m,source:"manual",_dbId:dbId})].sort((a,b)=>a.date.localeCompare(b.date)));
        }
        setModal(null);
      }}/></Modal>}
      {modal?.type==="editAnalysisValue"&&(()=>{
        const {h, selAnalyte, analysisDocs, onUpdateAnalysisValue} = modal.data||{};
        return <Modal title={`${tr("Editează:")} ${selAnalyte}`} onClose={()=>setModal(null)}>
          <EditAnalysisValueForm initial={h} analysisDocs={analysisDocs} selAnalyte={selAnalyte}
            onSave={async(newVal)=>{
              const ad = analysisDocs.find(a=>a.docId===h.docId);
              if(!ad) return;
              // Update the value in its doc (name, date stored per-value)
              const newValues = (ad.values||[]).map((v,vi)=>vi===h.valueIndex?{...v,...newVal}:v);
              // If date changed, update the analysisDoc date too (only if all values share same doc)
              const newDate = newVal.date && newVal.date!==ad.date ? newVal.date : ad.date;
              if(onUpdateAnalysisValue) await onUpdateAnalysisValue(h.docId, newValues, newDate);
              setModal(null);
            }}
          />
        </Modal>;
      })()}
      {modal?.type==="interpretAnalyses"&&(()=>{
        const {latest, analyteMap} = modal.data||{};
        return <Modal title={tr("Interpretare AI a analizelor")} onClose={()=>setModal(null)} wide>
          <InterpretAnalysesForm latest={latest} analyteMap={analyteMap} callAI={callAI} patient={patient}/>
        </Modal>;
      })()}
      {modal?.type==="compareImaging"&&(()=>{
        const {imgList} = modal.data||{};
        return <Modal title={tr("Compară studii imagistice")} onClose={()=>setModal(null)} wide>
          <CompareImagingForm imgList={imgList} callAI={callAI}
            savedCompare={imagingCompare}
            onSaveCompare={(cmp)=>{
              setImagingCompare(cmp);
              // Salvează cu cheie pe combinația de studii (păstrează toate comparațiile)
              const key = 'imaging_compare_' + (cmp.docIds||[]).slice().sort().join('_').slice(0,80);
              saveAISummary(key, cmp, session, activeProfileId);
              saveAISummary('imaging_compare', cmp, session, activeProfileId); // ultima pt afișare rapidă
            }}/>
        </Modal>;
      })()}
      {modal?.type==="addAnalysisManual"&&(()=>{
        return <Modal title={tr("Adaugă analiză manual")} onClose={()=>setModal(null)}>
          <AddAnalysisManualForm
            initialName={modal.data?.analyteName}
            customAnalytes={patient.customAnalytes||[]}
            onAddCustomAnalyte={(name)=>{
              setPatient(p=>{ const ca=p.customAnalytes||[]; if(ca.includes(name)) return p; const np={...p,customAnalytes:[...ca,name]}; savePatientNow(np); return np; });
            }}
            onDeleteCustomAnalyte={(name)=>{
              setPatient(p=>{ const np={...p,customAnalytes:(p.customAnalytes||[]).filter(x=>x!==name)}; savePatientNow(np); return np; });
            }}
            onSave={async(entry)=>{
              // Salvează ca analysisDoc cu docId special "manual_<timestamp>"
              const docId = "manual_" + Date.now();
              const newAD = {docId, docName:tr("Adăugare"), date:entry.date, values:[{
                name:entry.name, value:entry.value, unit:entry.unit,
                referenceRange:entry.referenceRange||"",
                referenceMin:entry.referenceMin||"", referenceMax:entry.referenceMax||"",
                status:entry.status||"normal"
              }]};
              await saveAnalysisDoc(newAD, session, activeProfileId, sharedOwnerId);
              setAnalysisDocs(p=>[...p, newAD]);
              setModal(null);
            }}
          />
        </Modal>;
      })()}
      {modal?.type==="editMed"&&<Modal title={tr("Editează medicament")} onClose={()=>setModal(null)} wide>
        <AddMedForm initial={modal.data} onAdd={async m=>{
          const updated={...(modal.data||{}),...m,id:modal.data?.id,color:modal.data?.color};
          await saveMedication(updated,session,activeProfileId,sharedOwnerId);
          setMedsState(p=>p.map(x=>x.id===modal.data?.id?{...updated,_dbId:x._dbId}:x));
          // Aplică doza/orarul nou de azi înainte. Trecutul și zilele cu intrări reale rămân neatinse.
          const medId=modal.data?.id;
          const todayStr=todayISO();
          setMedLogsState(p=>{
            const out={...p};
            const updatedMeds=[{...updated}];
            // Regenerează pentru zilele care există deja în log + următoarele 62 de zile de azi
            const daysToProcess=new Set(Object.keys(out).filter(d=>d>=todayStr));
            for(let i=0;i<62;i++){ daysToProcess.add(addDaysISO(todayStr,i)); }
            daysToProcess.forEach(day=>{
              if(day<todayStr) return; // trecutul rămâne neatins
              const dayLog=out[day]||{};
              const cur=dayLog[medId]||[];
              // intrările reale (confirmate/sărite/manuale) rămân; nu atingem zilele cu astfel de intrări confirmate
              const hasConfirmed=cur.some(x=>x.status==="done"||x.status==="skipped");
              if(hasConfirmed) return;
              const historic=cur.filter(isHistoricDose);
              const fresh=shouldMedBeActiveOnDate(updated, day) ? (buildDayLog(updatedMeds, day)[medId]||[]) : [];
              const newDayLog={...dayLog};
              if(fresh.length || historic.length) newDayLog[medId]=[...fresh,...historic];
              else delete newDayLog[medId];
              out[day]=newDayLog;
              saveMedLog(day,newDayLog,session,activeProfileId,sharedOwnerId);
            });
            return out;
          });
          setModal(null);
        }} onDelete={()=>{
          const atRisk = medHasUnnamedHistory(modal.data?.id, medLogs);
          const msg = atRisk
            ? `${modal.data?.name} ${tr("are intrări confirmate mai vechi care nu pot fi păstrate. Dacă îl ștergi, acele intrări vor dispărea din istoric. Continui?")}`
            : `${tr("Ștergi")} ${modal.data?.name}? ${tr("Intrările confirmate rămân în istoric.")}`;
          confirmDelete(msg,async()=>{
            if(modal.data?._dbId){
              try { await deleteMedication(modal.data?._dbId,session,sharedOwnerId); }
              catch(e){
                window.fmErr && window.fmErr('deleteMedication failed:', e);
                fmAlert(`${tr("Nu am putut șterge")} "${modal.data?.name}" ${tr("din baza de date. Verifică conexiunea și încearcă din nou.")}`);
                return;
              }
            }
            const delId=modal.data?.id;
            setMedLogsState(p=>{
              const u={};
              Object.entries(p).forEach(([d,l])=>{
                const doses=l[delId]||[];
                const hasHistoric=doses.some(isHistoricDose);
                // Păstrează ziua dacă are intrări de istoric (confirmate sau manuale); altfel scoate medicamentul
                if(hasHistoric){ u[d]=l; }
                else { const nl={...l}; delete nl[delId]; u[d]=nl; }
              });
              return u;
            });
            setMedsState(ms=>ms.filter(m=>m.id!==modal.data?.id));
            setModal(null);
          });
        }}/>
      </Modal>}
      {modal?.type==="addMed"&&<Modal title={tr("Medicament nou")} onClose={()=>setModal(null)} wide><AddMedForm onAdd={async m=>{const med={...m,id:Date.now(),active:true,color:PALETTE[meds.length%PALETTE.length]};const dbId=await saveMedication(med,session,activeProfileId,sharedOwnerId);setMedsState(p=>[...p,{...med,_dbId:dbId}]);setModal(null);}}/></Modal>}
      {modal?.type==="addEvent"&&<Modal title={tr("Programare nouă")} onClose={()=>setModal(null)} wide><AddEventForm initial={{date:modal.data?.date||todayK}} doctors={doctors} events={events} onAdd={async ev=>{const edk=ev.date||todayK;await saveEventsAndUpdate(edk, evs=>[...evs,stampAuthor({...ev,id:Date.now(),visitNote:ev.visitNote||"",editing:false})]);setModal(null);}}/></Modal>}
      {modal?.type==="exportTratament"&&(
        <Modal title={tr("Export Plan Tratament")} onClose={()=>setModal(null)}>
          <ExportTratamentForm meds={meds} medLogs={medLogs} symptoms={symptoms} patient={patient} metrics={metrics} events={events} onClose={()=>setModal(null)}/>
        </Modal>
      )}
      {modal?.type==="exportRaport"&&(
        <Modal title={tr("Raport medical complet")} onClose={()=>setModal(null)}>
          <ExportRaportComplet meds={meds} medLogs={medLogs} symptoms={symptoms} patient={patient} metrics={metrics} events={events} analysisDocs={analysisDocs} onClose={()=>setModal(null)}/>
        </Modal>
      )}
      {modal?.type==="addNote"&&(
        <Modal title={tr("Adaugă notiță")} onClose={()=>{ try{localStorage.removeItem("fm_draft_note_"+(modal?.data?.selDay||todayK));}catch(e){} setModal(null); }}>
          <AddNoteForm selDay={modal?.data?.selDay||todayK} onAdd={async note=>{
            const day = modal?.data?.selDay||todayK;
            await saveEventsAndUpdate(day, evs=>[...evs,stampAuthor({id:Date.now(),spec:"Notițe",title:"Notiță",time:"",dr:"",hospital:"",address:"",phone:"",visitNote:note,editing:false,isNote:true})]);
            setModal(null);
          }}/>
        </Modal>
      )}
      {modal?.type==="addSymptom"&&<Modal title={tr("Simptome")} onClose={()=>setModal(null)}><AddSymptomForm initial={modal.data} customSymptoms={patient.customSymptoms||[]} onAddCustomSymptom={(name)=>{ setPatient(p=>{ const cs=p.customSymptoms||[]; if(cs.includes(name)||SYMPTOMS_LIST.includes(name))return p; const np={...p,customSymptoms:[...cs,name]}; savePatientNow(np); return np; }); }} onDeleteCustomSymptom={(name)=>{ setPatient(p=>{ const np={...p,customSymptoms:(p.customSymptoms||[]).filter(x=>x!==name)}; savePatientNow(np); return np; }); }} onAdd={async s=>{const stamped=stampAuthor({...s});const dbId=await saveSymptom(stamped,session,activeProfileId,sharedOwnerId);setSymptomsState(p=>[...p,{...stamped,id:dbId||Date.now()}]);setModal(null);}}/></Modal>}
      {modal?.type==="editSymptom"&&<Modal title={tr("Editează simptom")} onClose={()=>setModal(null)}><AddSymptomForm initial={modal.data} customSymptoms={patient.customSymptoms||[]} onAddCustomSymptom={(name)=>{ setPatient(p=>{ const cs=p.customSymptoms||[]; if(cs.includes(name)||SYMPTOMS_LIST.includes(name))return p; const np={...p,customSymptoms:[...cs,name]}; savePatientNow(np); return np; }); }} onDeleteCustomSymptom={(name)=>{ setPatient(p=>{ const np={...p,customSymptoms:(p.customSymptoms||[]).filter(x=>x!==name)}; savePatientNow(np); return np; }); }} onAdd={async s=>{
        setSymptomsState(p=>p.map(x=>x.id===modal.data?.id ? {...x,...s} : x));
        await updateSymptom(modal.data?.id, s, session, sharedOwnerId);
        setModal(null);
      }}/>
      <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid var(--border)"}}>
        <button className="bdel" style={{width:"100%",padding:"9px",fontSize:13}} onClick={()=>confirmDelete(undefined,async()=>{
          try { await deleteSymptom(modal.data?.id,session,sharedOwnerId); }
          catch(e){ window.fmErr && window.fmErr('deleteSymptom failed:', e); fmAlert('Nu am putut șterge simptomul din baza de date. Încearcă din nou.'); return; }
          setSymptomsState(p=>p.filter(x=>x.id!==modal.data?.id));
          setModal(null);
        })}><IconTrash size={14}/>{tr(" Șterge simptom")}</button>
      </div>
      </Modal>}
      {modal?.type==="addFamily"&&<Modal title={modal.data?._dbId?tr("Editează"):tr("Adaugă membru familie")} onClose={()=>setModal(null)} wide><AddFamilyForm initial={modal.data} checkRole={async(email)=>await getActiveRoleForEmail(session, activeProfileId, email)} onAdd={async f=>{
        let profileId = f.profileId || null;
        if(f.createProfile && !profileId) {
          const p = await createProfile(session, f.name, f.relation, f.dob, f.sex||'');
          if(p) { profileId = p.id; setProfiles(prev=>[...prev,p]); }
        }
        const memberToSave = {...f, profileId};
        const dbId = await saveFamily(memberToSave, session, activeProfileId);
        setFamilyState(p=>f._dbId?p.map(x=>x._dbId===f._dbId?{...memberToSave,_dbId:f._dbId}:x):[...p,{...memberToSave,_dbId:dbId}]);

        // Sincronizare bidirecțională
        const targetProfileId = profileId || (profiles.find(p=>p.name===f.name)?.id);
        const myName = patient.name || profiles.find(p=>p.id===activeProfileId)?.name || "";
        const myDob = patient.dob || profiles.find(p=>p.id===activeProfileId)?.dob || "";
        const mySex = profiles.find(p=>p.id===activeProfileId)?.sex || patient.sex || "";
        const inverseRelation = getInverseRelation(f.relation, mySex);

        if(targetProfileId) {
          const inverseEntry = {name:myName, relation:inverseRelation, dob:myDob, email:session?.user?.email||"", shareAccess:false, caregiver:false, conditions:[], profileId:activeProfileId||null};
          const existingFamily = await loadFamily(session, targetProfileId);
          const normN = s => (s||"").normalize("NFD").replace(/[\u0300-\u036f]/g,"").trim().toLowerCase();
          const alreadyExists = existingFamily.some(x=>normN(x.name)===normN(myName));
          if(!alreadyExists) await saveFamily(inverseEntry, session, targetProfileId);

          // Notificare rude de grad 2
          if(!f._dbId) {
            if(targetProfileId) {
              const myFamilyMember = family.find(x=>x.profileId===targetProfileId);
              if(myFamilyMember) {
                const deg2Relation = getSecondDegreeRelation(myFamilyMember.relation, f.relation);
                if(deg2Relation) {
                  setModal({type:"suggestFamilyLink", data:{
                    name: f.name, dob: f.dob||"", relation: deg2Relation,
                    via: myFamilyMember.name, profileId: profileId||null
                  }});
                  return;
                }
              }
            }
          }
        } else if(!profileId && activeProfileId) {
          // Membrul adăugat (ex: Irina) nu are profil propriu
          // Adăugăm legătura inversă în familia profilului curent (ex: Viorel)
          // prin a pune Viorel în familia "rădăcină" a utilizatorului
          const mainFamily = await loadFamily(session, null);
          const profileName = profiles.find(p=>p.id===activeProfileId)?.name || "";
          // Comparație normalizată (fără diacritice, case-insensitive) ca să nu dubleze
          const norm = s => (s||"").normalize("NFD").replace(/[\u0300-\u036f]/g,"").trim().toLowerCase();
          const alreadyExists = profileName && mainFamily.some(x=>
            (x.profileId===activeProfileId) || (norm(x.name)===norm(profileName))
          );
          if(!alreadyExists && profileName) {
            const viorelEntry = {name:profileName, relation:inverseRelation, dob:myDob, email:"", shareAccess:false, caregiver:false, conditions:[], profileId:activeProfileId};
            const newDbId = await saveFamily(viorelEntry, session, null); // ID REAL din DB, nu Date.now()
            if(newDbId) setFamilyState(prev=>[...prev, {...viorelEntry, _dbId:newDbId}]);
          }
        }

        // Gestionare acces îngrijitor/medic
        const hasAccess = f.caregiver || f.isDoctor;
        if(f.email) {
          const role = f.caregiver ? 'caregiver' : (f.isDoctor ? 'doctor' : null);
          if(hasAccess && role) {
            // Verifică dacă emailul are deja celălalt rol (exclusivitate)
            const existingRole = await getActiveRoleForEmail(session, activeProfileId, f.email);
            if(existingRole && existingRole !== role) {
              const existingLabel = existingRole === 'doctor' ? tr('medic') : tr('îngrijitor');
              fmAlert(`${tr("Adresa")} ${f.email} ${tr("are deja rol de")} ${existingLabel} ${tr("pentru acest profil. O persoană nu poate fi și îngrijitor și medic simultan. Revocă întâi celălalt rol.")}`);
              return;
            }
            const profileName = patient.name || profiles.find(p=>p.id===activeProfileId)?.name || "Profil";
            const myName = patient.name || session?.user?.email?.split("@")[0] || "";
            const shareSettings = f.caregiver
              ? {fisa:true,tratament:true,analize:true,documente:true,medici:true,vaccinuri:true,familie:true,sumarAI:true}
              : (f.shareSettings || {fisa:true,tratament:true,analize:false,documente:false,medici:false,vaccinuri:false,familie:false,sumarAI:false});

            // Caută invitație existentă sau creează una nouă (link o singură dată)
            const inv = await getOrCreateInvitation(session, activeProfileId, f.email, role, shareSettings);
            if(inv) {
              // Verifică dacă persoana are deja cont → acces automat fără link
              const autoAccepted = await autoAcceptIfUserExists(inv.token);

              if(autoAccepted) {
                // Persoana are cont — acces dat automat, fără link
                if(!inv._existing) {
                  fmAlert(`✓ ${f.name} ${tr("are deja cont și a primit acces automat.")}`);
                }
                setModal(null);
              } else if(inv._existing || inv._reactivated) {
                // Invitația există deja (link trimis anterior) — nu mai arătăm link
                if(inv._reactivated) fmAlert(`✓ Acces reactivat pentru ${f.name}.`);
                setModal(null);
              } else {
                // Invitație nouă, persoana nu are cont — arătăm linkul
                sendInvitationEmail(f.email, myName, profileName, role, inv.token).catch(()=>{});
                const inviteUrl = `https://fisa-medicala.pages.dev?invite=${inv.token}`;
                setModal({type:"showInviteLink", data:{url:inviteUrl, email:f.email, name:f.name}});
              }
              return;
            }
          } else if(!hasAccess) {
            // Toggle Îngrijitor debifat — revocă DOAR rolul de îngrijitor (medicul se gestionează în tab Medici)
            await revokeInvitation(session, activeProfileId, f.email, 'caregiver');
          }
        }
        setModal(null);
      }}/></Modal>}

      {modal?.type==="showInviteLink"&&(
        <Modal title={tr("Link invitație")} onClose={()=>setModal(null)}>
          <div style={{padding:"4px 0"}}>
            <div style={{fontSize:13,color:"var(--text-2)",lineHeight:1.6,marginBottom:16}}>
              Trimite acest link lui <strong style={{color:"var(--text)"}}>{modal.data.name}</strong> (prin WhatsApp, SMS sau email). Apăsând pe el, va putea accesa profilul.
            </div>
            <div style={{background:"var(--bg)",border:"1px solid var(--border-2)",borderRadius:8,padding:"12px",fontSize:12,color:"var(--blue)",wordBreak:"break-all",marginBottom:16,fontFamily:"monospace"}}>
              {modal.data.url}
            </div>
            <div style={{display:"flex",gap:8}}>
              <button className="bp" style={{flex:1}} onClick={()=>{
                navigator.clipboard?.writeText(modal.data.url).then(()=>fmAlert(tr("Link copiat!"))).catch(()=>{});
              }}>{tr("📋 Copiază link")}</button>
              <button className="bg" style={{flex:1}} onClick={()=>{
                const text = `${tr("Salut! Te invit să accesezi profilul medical:")} ${modal.data.url}`;
                if(navigator.share) navigator.share({text}).catch(()=>{});
                else { navigator.clipboard?.writeText(text); fmAlert(tr("Text copiat pentru partajare!")); }
              }}>{tr("↗ Trimite")}</button>
            </div>
            <div style={{fontSize:11,color:"var(--text-3)",marginTop:12,textAlign:"center"}}>
              Am încercat și trimiterea pe email — dacă nu sosește, folosește linkul de mai sus.
            </div>
          </div>
        </Modal>
      )}

      {modal?.type==="suggestFamilyLink"&&(
        <Modal title={tr("Relație de familie detectată")} onClose={()=>setModal(null)}>
          <div style={{textAlign:"center",padding:"8px 0 20px"}}>
            <div style={{fontSize:40,marginBottom:12}}>👨‍👩‍👧</div>
            <div style={{fontSize:15,lineHeight:1.7,marginBottom:20}}>
              <strong>{modal.data?.name}</strong> {tr("a fost adăugat de")} <strong>{modal.data?.via}</strong>.<br/>
              <span style={{color:"var(--text-2)"}}>{tr("Vrei să îl/o adaugi și tu ca")} <strong style={{color:"var(--blue)"}}>{tr(modal.data?.relation)}</strong>?</span>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"center"}}>
              <button className="bp" onClick={async()=>{
                const newMember = {name:modal.data?.name, relation:modal.data?.relation, dob:modal.data?.dob||"", email:"", shareAccess:false, caregiver:false, conditions:[], profileId:modal.data?.profileId||null};
                const dbId = await saveFamily(newMember, session, activeProfileId||null);
                setFamilyState(p=>[...p,{...newMember,_dbId:dbId}]);
                setModal(null);
              }}>Da, adaugă ca {modal.data?.relation}</button>
              <button className="bg" onClick={()=>setModal(null)}>{tr("Nu, mulțumesc")}</button>
            </div>
          </div>
        </Modal>
      )}

      {processing&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.85)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:300}}>
          <HeartSpinner size={58} label={processingMsg}/>
        </div>
      )}
    </div>
  );
}


